// Move import to top
import { imageUrlToBase64DataUri } from './utilityService';

// Import the compatibility matrix as the single source of truth
import { compatibilityMatrix } from './compatibilityMatrix';

// Constants for SVG generation
const PX_PER_INCH = 6;
const MAX_VISUAL_LENGTH = 300;
const connW = 80; // Doubled from 40
const connH = 80; // Doubled from 40

// Enhanced color palette for better visual hierarchy
// Note: These constants are defined for future use but currently unused
// const CABLE_COLORS = {
//   audio: '#2563eb',      // Modern blue
//   power: '#1f2937',      // Dark gray
//   dmx: '#92400e',        // Brown
//   video: '#ea580c',      // Orange
//   data: '#059669',       // Green
//   hybrid: '#7c3aed'      // Purple
// };

// const CONNECTOR_COLORS = {
//   audio: '#3b82f6',      // Lighter blue
//   power: '#374151',      // Medium gray
//   dmx: '#a16207',        // Lighter brown
//   video: '#f97316',      // Lighter orange
//   data: '#10b981',       // Lighter green
//   hybrid: '#8b5cf6'      // Lighter purple
// };

// Enhanced shadow and gradient definitions
// Note: This constant is defined for future use but currently unused
// const SVG_DEFS = `
//   <defs>
//     <!-- Enhanced shadow filter -->
//     <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
//       <feDropShadow dx="3" dy="4" stdDeviation="4" floodColor="#000" floodOpacity="0.25" />
//     </filter>
//     
//     <!-- Subtle shadow for connectors -->
//     <filter id="connectorShadow" x="-20%" y="-20%" width="140%" height="140%">
//       <feDropShadow dx="1" dy="2" stdDeviation="2" floodColor="#000" floodOpacity="0.2" />
//     </filter>
//     
//     <!-- Cable gradient -->
//     <linearGradient id="cableGradient" x1="0%" y1="0%" x2="100%" y2="0%">
//       <stop offset="0%" style="stop-color:#f8fafc;stop-opacity:1" />
//       <stop offset="50%" style="stop-color:#e2e8f0;stop-opacity:1" />
//       <stop offset="100%" style="stop-color:#f8fafc;stop-opacity:1" />
//     </linearGradient>
//     
//     <!-- Connector gradient -->
//     <linearGradient id="connectorGradient" x1="0%" y1="0%" x2="0%" y2="100%">
//       <stop offset="0%" style="stop-color:#ffffff;stop-opacity:1" />
//       <stop offset="100%" style="stop-color:#f1f5f9;stop-opacity:1" />
//     </linearGradient>
//   </defs>
// `;

// Helper function to get connector image based on configuration
function getConnectorImage(connector, configuration) {
  if (!connector) {
    return null;
  }

  // First, try to get the connector data from the compatibility matrix
  let connectorId = connector.id || connector.connector;
  
  // If we have a connector ID, look it up in the compatibility matrix
  if (connectorId && compatibilityMatrix[connectorId]) {
    const matrixData = compatibilityMatrix[connectorId];
    
    // Return the image from the compatibility matrix
    if (matrixData.image) {
      return matrixData.image;
    }
  }
  
  // If the connector ID wasn't found in the matrix, try to find a matching connector
  // based on family, type, gender, and configuration
  if (connector.family || connector.type) {
    // Search through the compatibility matrix for a matching connector
    for (const [, matrixConnector] of Object.entries(compatibilityMatrix)) {
      // Check if this connector matches our criteria
      const familyMatch = !connector.family || matrixConnector.family === connector.family;
      const typeMatch = !connector.type || matrixConnector.type === connector.type;
      const genderMatch = !connector.gender || matrixConnector.gender === connector.gender;
      
      // Check mount type / configuration match
      let mountTypeMatch = true;
      if (configuration) {
        const targetMountType = configuration === 'standard' ? 'cable' : 
                               configuration === 'panel-mount' ? 'panel' : 
                               configuration === 'right-angle' ? 'rightangle' : configuration;
        
        mountTypeMatch = matrixConnector.mountType === targetMountType || 
                        (matrixConnector.mountType === 'right-angle' && targetMountType === 'rightangle');
      }
      
      if (familyMatch && typeMatch && genderMatch && mountTypeMatch && matrixConnector.image) {
        return matrixConnector.image;
      }
    }
  }
  
  // Enhanced fallback logic based on connector properties
  if (connector.family || connector.type || connector.label) {
    const family = (connector.family || '').toLowerCase();
    const type = (connector.type || '').toLowerCase();
    const label = (connector.label || '').toLowerCase();
    const gender = (connector.gender || '').toLowerCase();
    
    // XLR connectors
    if (family.includes('xlr') || type.includes('xlr') || label.includes('xlr') || connectorId?.includes('NC3') || connectorId?.includes('NC5')) {
      if (configuration === 'right-angle') {
        return gender === 'male' ? '/images/nc3mrx.jpg' : '/images/nc3frx.jpg';
      } else if (configuration === 'panel-mount') {
        return gender === 'male' ? '/images/nc3md-l-1.jpg' : '/images/nc3fd-L-1.jpg';
      } else {
        return gender === 'male' ? '/images/nc3mxx.jpg' : '/images/nc3fxx.jpg';
      }
    }
    
    // Speakon connectors - use actual connector images from compatibility matrix
    if (family.includes('speakon') || type.includes('speakon') || label.includes('speakon') || connectorId?.includes('NL')) {
      // The compatibility matrix lookup above should handle this, but fallback to specific images if needed
      if (connectorId?.includes('NL2')) {
        return '/images/NL2FXX-W-S.png';
      } else if (connectorId?.includes('NL4')) {
        return '/images/NL4FXX-W-S.png';
      } else if (connectorId?.includes('NL8')) {
        return '/images/NL8FC.jpg';
      } else {
        return '/images/NL4FXX-W-S.png'; // Default to NL4FXX-W-S
      }
    }
    
    // 1/4" TS connectors
    if (family.includes('ts') || type.includes('ts') || label.includes('ts') || label.includes('1/4') || connectorId?.includes('2T')) {
      if (configuration === 'right-angle') {
        return '/images/1_4_ TS-RA (ACPM-TN).png';
      } else if (configuration === 'panel-mount') {
        return '/images/1_4_ TS (ACPM-GN).png';
      } else {
        return '/images/1_4_ TS (ACPM-GN).png';
      }
    }
    
    // 1/4" TRS connectors
    if (family.includes('trs') || type.includes('trs') || label.includes('trs') || label.includes('3.5mm')) {
      if (configuration === 'right-angle') {
        return '/images/1_4_ TRS-RA (ACPS-TN).png';
      } else if (configuration === 'panel-mount') {
        return '/images/1_4_ TRS (ACPS-GN).png';
      } else {
        return '/images/1_4_ TRS (ACPS-GN).png';
      }
    }
    
    // RCA connectors
    if (family.includes('rca') || type.includes('rca') || label.includes('rca')) {
      return gender === 'male' ? '/images/RCA_male.png' : '/images/RCA_female.png';
    }
    
    // IEC connectors
    if (family.includes('iec') || type.includes('iec') || label.includes('iec') || connectorId?.includes('IEC')) {
      if (configuration === 'panel-mount') {
        return gender === 'male' ? '/images/IEC_C14_panel_mount_male.png' : '/images/IEC_C13_panel_mount_female.png';
      } else {
        return gender === 'male' ? '/images/IEC_C14_cable_mount_male.png' : '/images/IEC_C13_cable_mount_female.png';
      }
    }
    
    // NEMA 5-15 connectors
    if (family.includes('nema') || type.includes('nema') || label.includes('nema') || label.includes('5-15') || connectorId?.includes('5-15')) {
      if (configuration === 'panel-mount') {
        return gender === 'male' ? '/images/515P_M.jpg' : '/images/515R_F.jpg';
      } else {
        return gender === 'male' ? '/images/5-15P_cable_mount_male.png' : '/images/5-15R_cable_mount_female.png';
      }
    }
    
    // PowerCON connectors
    if (family.includes('powercon') || type.includes('powercon') || label.includes('powercon') || connectorId?.includes('PowerCon')) {
      if (family.includes('blue') || connectorId?.includes('Blue')) {
        return '/images/NAC3FXXA-W-S.png';
      } else {
        return '/images/NAC3FXXB-W-S.png';
      }
    }
    
    // True1 connectors
    if (family.includes('true1') || type.includes('true1') || label.includes('true1') || connectorId?.includes('True1')) {
      if (configuration === 'panel-mount') {
        return gender === 'male' ? '/images/True1_panel_mount_male.png' : '/images/True1_panel_mount_female.png';
      } else {
        return gender === 'male' ? '/images/True1_cable_mount_male.png' : '/images/True1_cable_mount_female.png';
      }
    }
  }
  
  // If no match found, return null to use the modern placeholder
  return null;
}

// Generate SVG for HybridPowerAudioAudio cables
export function generateHybridPowerAudioAudioSVG({
  connectors = {},
  lengths = {},
  unit = 'in',
  svgProps = {},
  asString = false
}) {
  // Calculate visual lengths with consistent scaling
  const cLen = PX_PER_INCH * (parseFloat(lengths['C']) || 0);
  const dLen = PX_PER_INCH * (parseFloat(lengths['D']) || 0);
  const eLen = PX_PER_INCH * (parseFloat(lengths['E']) || 0);
  const fLen = PX_PER_INCH * (parseFloat(lengths['F']) || 0);
  const gLen = PX_PER_INCH * (parseFloat(lengths['G']) || 0);
  const hLen = PX_PER_INCH * (parseFloat(lengths['H']) || 0);
  
  // Apply visual length caps for reasonable SVG display
  const cVisual = Math.min(cLen, MAX_VISUAL_LENGTH);
  const dVisual = Math.min(dLen, MAX_VISUAL_LENGTH);
  const eVisual = Math.min(eLen, MAX_VISUAL_LENGTH);
  const fVisual = Math.min(fLen, MAX_VISUAL_LENGTH);
  const gVisual = Math.min(gLen, MAX_VISUAL_LENGTH);
  const hVisual = Math.min(hLen, MAX_VISUAL_LENGTH);
  
  // Calculate endpoints with proper scaling using the same approach as HybridPowerAudioAudioConnectorSelection
  const I_end_adj = { x: 200 - cVisual - 50, y: 120 }; // Audio1
  const J_end_adj = { x: 750 + dVisual + 50, y: 120 };  // Audio1
  const K_end_adj = { x: 200 - eVisual - 100, y: 250 };  // Power
  const L_end_adj = { x: 750 + fVisual + 100, y: 250 };  // Power
  const M_end_adj = { x: 200 - gVisual - 50, y: 380 };  // Audio2
  const N_end_adj = { x: 750 + hVisual + 50, y: 380 };  // Audio2
  
  // Auto-scaling logic - simplified for better centering
  const pad = 30;
  const minX = Math.min(I_end_adj.x, K_end_adj.x, M_end_adj.x);
  const maxX = Math.max(J_end_adj.x, L_end_adj.x, N_end_adj.x);
  const contentWidth = maxX - minX;
  
  // Calculate actual content bounds including labels - ensure A label isn't cut off
  const minY = Math.min(I_end_adj.y, J_end_adj.y, 40) - 40 - 15; // Include overall label position
  const maxY = Math.max(M_end_adj.y, N_end_adj.y) + 40 + 15; // Bottom connectors + label
  const contentHeight = maxY - minY;
  
  // Calculate viewBox for proper centering with adequate padding for labels
  const viewBoxWidth = contentWidth + 2 * pad;
  const viewBoxHeight = contentHeight + 2 * pad; // More padding for labels
  const viewBoxX = minX - pad;
  const viewBoxY = minY - pad; // Full padding at top for A label
  
  // Dynamic label positions - positioned along each wire path using visual lengths
  const cLabel = { x: 200 - (cVisual * 0.6), y: 200 - (cVisual * 0.3) }; // Along C wire path
  const dLabel = { x: 750 + (dVisual * 0.6), y: 200 - (dVisual * 0.3) }; // Along D wire path
  const eLabel = { x: 200 - (eVisual * 0.6), y: 251 }; // Along E wire path (horizontal)
  const fLabel = { x: 750 + (fVisual * 0.6), y: 251 }; // Along F wire path (horizontal)
  const gLabel = { x: 200 - (gVisual * 0.6), y: 300 + (gVisual * 0.3) }; // Along G wire path
  const hLabel = { x: 750 + (hVisual * 0.6), y: 300 + (hVisual * 0.3) }; // Along H wire path
  
  const overallLen = parseFloat(lengths['A']) || 0;
  
  // Helper function to get connector image based on configuration (copied from HybridPowerAudioAudioConnectorSelection)
  const getConnectorImage = (connector, configuration) => {
    if (!connector?.image) return connector?.image;
    
    // Handle XLR connectors (3-pin and 5-pin)
    if (connector.family === 'XLR' || connector.id?.startsWith('X') || connector.id?.startsWith('NC3') || connector.id?.startsWith('NC5')) {
      if (configuration === 'right-angle') {
        if (connector.gender?.toLowerCase() === 'male') {
          return '/images/nc3mrx.jpg';
        }
        if (connector.gender?.toLowerCase() === 'female') {
          return '/images/nc3frx.jpg';
        }
      } else if (configuration === 'panel-mount') {
        if (connector.gender?.toLowerCase() === 'male') {
          return '/images/nc3md-l-1.jpg';
        }
        if (connector.gender?.toLowerCase() === 'female') {
          return '/images/nc3fd-L-1.jpg';
        }
      }
      // Standard XLR - return the original image
      return connector.image;
    }
    
    // Handle 1/4" TS connectors
    if (connector.family === 'TS' || connector.id === '2T_M' || connector.id === '2T_F') {
      if (connector.gender?.toLowerCase() === 'male') {
        if (configuration === 'right-angle') {
          return '/images/1_4_ TS-RA (ACPM-TN).png';
        } else if (configuration === 'panel-mount') {
          return '/images/coming_soon.png'; // Placeholder for panel mount
        } else {
          return '/images/1_4_ TS (ACPM-GN).png'; // Standard
        }
      } else {
        // All female variants use placeholder
        return '/images/coming_soon.png';
      }
    }
    
    // Handle 1/4" TRS connectors
    if (connector.family === 'TRS' || connector.id === '2TR_M' || connector.id === '2TR_F') {
      if (connector.gender?.toLowerCase() === 'male') {
        if (configuration === 'right-angle') {
          return '/images/1_4_ TRS-RA (ACPS-TN).png';
        } else if (configuration === 'panel-mount') {
          return '/images/coming_soon.png'; // Placeholder for panel mount
        } else {
          return '/images/1_4_ TRS (ACPS-GN).png'; // Standard
        }
      } else {
        // All female variants use placeholder
        return '/images/coming_soon.png';
      }
    }
    
    // Handle 3.5mm TS connectors
    if (connector.id === '3T_M' || connector.id === '3T_F') {
      if (connector.gender?.toLowerCase() === 'male') {
        if (configuration === 'right-angle') {
          return '/images/coming_soon.png'; // Placeholder for right angle
        } else if (configuration === 'panel-mount') {
          return '/images/coming_soon.png'; // Placeholder for panel mount
        } else {
          return '/images/TS_3_5mm_male.png'; // Standard
        }
      } else {
        // All female variants use placeholder
        return '/images/coming_soon.png';
      }
    }
    
    // Handle 3.5mm TRS connectors
    if (connector.id === '3TR_M' || connector.id === '3TR_F') {
      if (connector.gender?.toLowerCase() === 'male') {
        if (configuration === 'right-angle') {
          return '/images/coming_soon.png'; // Placeholder for right angle
        } else if (configuration === 'panel-mount') {
          return '/images/coming_soon.png'; // Placeholder for panel mount
        } else {
          return '/images/TRS_3_5mm_male.png'; // Standard
        }
      } else {
        if (configuration === 'right-angle') {
          return '/images/coming_soon.png'; // Placeholder for right angle
        } else if (configuration === 'panel-mount') {
          return '/images/coming_soon.png'; // Placeholder for panel mount
        } else {
          return '/images/TRS_3_5mm_female.png'; // Standard
        }
      }
    }
    
    // Handle RCA connectors
    if (connector.family === 'RCA' || connector.id === 'RCA_M' || connector.id === 'RCA_F') {
      if (configuration === 'right-angle') {
        return '/images/coming_soon.png'; // Placeholder for right angle
      } else if (configuration === 'panel-mount') {
        return '/images/coming_soon.png'; // Placeholder for panel mount
      } else {
        // Standard RCA - return the original image
        return connector.image;
      }
    }
    
    // Handle Speakon connectors (no right-angle variants available)
    if (connector.family === 'Speakon' || connector.id?.startsWith('SPK')) {
      if (configuration === 'right-angle') {
        return '/images/coming_soon.png'; // Placeholder for right angle (not available)
      } else if (configuration === 'panel-mount') {
        return '/images/coming_soon.png'; // Placeholder for panel mount
      } else {
        // Standard Speakon - return the original image
        return connector.image;
      }
    }
    
    // Handle PowerCON connectors
    if (connector.family?.includes('PowerCon') || connector.id?.includes('PowerCon')) {
      if (configuration === 'right-angle') {
        return '/images/coming_soon.png'; // Placeholder for right angle
      } else if (configuration === 'panel-mount') {
        if (connector.family === 'PowerCon_Blue' || connector.id?.includes('Blue')) {
          return '/images/NAC3MPXXA.jpg'; // Blue panel mount male
        } else {
          return '/images/NAC3MPXXB.jpg'; // Grey panel mount female
        }
      } else {
        // Standard configuration
        if (connector.family === 'PowerCon_Blue' || connector.id?.includes('Blue')) {
          return '/images/NAC3FXXA-W-S.png'; // Blue standard female
        } else {
          return '/images/NAC3FXXB-W-S.png'; // Grey standard male
        }
      }
    }
    
    // Handle IEC connectors
    if (connector.family?.includes('IEC') || connector.id?.includes('IEC')) {
      if (configuration === 'right-angle') {
        return '/images/coming_soon.png'; // Placeholder for right angle
      } else if (configuration === 'panel-mount') {
        if (connector.gender?.toLowerCase() === 'male') {
          return '/images/IEC_C14_panel_mount_male.png';
        } else if (connector.gender?.toLowerCase() === 'female') {
          return '/images/IEC_C13_panel_mount_female.png';
        }
      } else {
        // Standard IEC - return the original image
        return connector.image;
      }
    }
    
    // Handle NEMA 5-15 connectors
    if (connector.family?.includes('NEMA') || connector.id?.includes('5-15')) {
      if (configuration === 'right-angle') {
        return '/images/coming_soon.png'; // Placeholder for right angle
      } else if (configuration === 'panel-mount') {
        if (connector.gender?.toLowerCase() === 'male') {
          return '/images/515P_M.jpg';
        } else if (connector.gender?.toLowerCase() === 'female') {
          return '/images/515R_F.jpg';
        }
      } else {
        // Standard NEMA - return the original image
        return connector.image;
      }
    }
    
    // Handle True1 connectors
    if (connector.family?.includes('True1') || connector.id?.includes('True1')) {
      if (configuration === 'right-angle') {
        return '/images/coming_soon.png'; // Placeholder for right angle
      } else if (configuration === 'panel-mount') {
        return '/images/coming_soon.png'; // Placeholder for panel mount
      } else {
        // Standard True1 - return the original image
        return connector.image;
      }
    }
    
    // Default fallback - return the original image
    return connector.image;
  };
  
  // Generate the SVG content
  const svgContent = `
    <svg width="1000" height="500" viewBox="${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}" xmlns="http://www.w3.org/2000/svg" style="font-family: Arial, sans-serif;">
      <defs>
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="2" dy="3" stdDeviation="3" floodColor="#000" floodOpacity="0.3" />
        </filter>
      </defs>
      
      <g>
        <!-- Central wire (B) - Represents the main cable length -->
        <path 
          d="M 200 251 L 750 251" 
          stroke="#000" 
          strokeWidth="8" 
          fill="#000" 
          filter="url(#shadow)"
        />
        
        <!-- Cable sheath outline for better visibility -->
        <rect x="200" y="245" width="550" height="10" rx="5" fill="#000" stroke="#b32b2b" strokeWidth=".5" opacity="0.7" />
        
        <!-- Audio 1 branches (I, J) - Blue, thin with prettier curves -->
        <path d="M 200 251 C ${200 - 30} 230 ${200 - cVisual * 0.7} 190 ${200 - cVisual - 10} 160 C ${200 - cVisual - 25} 145 ${I_end_adj.x + 15} ${I_end_adj.y + 15} ${I_end_adj.x} ${I_end_adj.y}" stroke="#6495c8" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
        <path d="M 750 251 C ${750 + 30} 230 ${750 + dVisual * 0.7} 190 ${750 + dVisual + 10} 160 C ${750 + dVisual + 25} 145 ${J_end_adj.x - 15} ${J_end_adj.y + 15} ${J_end_adj.x} ${J_end_adj.y}" stroke="#6495c8" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
        
        <!-- Power branches (K, L) - Black, same thickness as audio with prettier curves -->
        <path d="M 200 251 C ${200 - 30} 251 ${200 - eVisual * 0.7} 251 ${200 - eVisual - 10} 251 C ${200 - eVisual - 25} 251 ${K_end_adj.x + 15} ${K_end_adj.y} ${K_end_adj.x} ${K_end_adj.y}" stroke="#000" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
        <path d="M 750 251 C ${750 + 30} 251 ${750 + fVisual * 0.7} 251 ${750 + fVisual + 10} 251 C ${750 + fVisual + 25} 251 ${L_end_adj.x - 15} ${L_end_adj.y} ${L_end_adj.x} ${L_end_adj.y}" stroke="#000" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
        
        <!-- Audio 2 branches (M, N) - Green, thin with prettier curves -->
        <path d="M 200 251 C ${200 - 30} 270 ${200 - gVisual * 0.7} 310 ${200 - gVisual - 10} 340 C ${200 - gVisual - 25} 355 ${M_end_adj.x + 15} ${M_end_adj.y - 15} ${M_end_adj.x} ${M_end_adj.y}" stroke="#4caf50" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
        <path d="M 750 251 C ${750 + 30} 270 ${750 + hVisual * 0.7} 310 ${750 + hVisual + 10} 340 C ${750 + hVisual + 25} 355 ${N_end_adj.x - 15} ${N_end_adj.y - 15} ${N_end_adj.x} ${N_end_adj.y}" stroke="#4caf50" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
        
        <!-- Connector points -->
        ${['I', 'J', 'K', 'L', 'M', 'N'].map(pointId => {
          const endPos = pointId === 'I' ? I_end_adj :
                        pointId === 'J' ? J_end_adj :
                        pointId === 'K' ? K_end_adj :
                        pointId === 'L' ? L_end_adj :
                        pointId === 'M' ? M_end_adj :
                        N_end_adj;
          const color = pointId === 'I' || pointId === 'J' ? '#6495c8' :
                       pointId === 'K' || pointId === 'L' ? '#000' :
                       '#4caf50';
          const connector = connectors[pointId];
          
          return `
            <g>
              ${connector?.image ? 
                `<image 
                  href="${getConnectorImage(connector, connector.configuration || 'standard') || connector.image}"
                  x="${endPos.x - connW / 2}" 
                  y="${endPos.y - connH / 2}" 
                  width="${connW}" 
                  height="${connH}" 
                  filter="url(#shadow)"
                />` :
                `<rect x="${endPos.x - connW / 2}" y="${endPos.y - connH / 2}" width="${connW}" height="${connH}" rx="6" fill="${color}" stroke="#fff" strokeWidth="3" filter="url(#shadow)" />`
              }
              <text 
                x="${endPos.x}" 
                y="${endPos.y + connH / 2 + 20}" 
                textAnchor="middle" 
                fontSize="14" 
                fill="#222" 
                fontWeight="bold"
              >
                ${pointId}
              </text>
            </g>
          `;
        }).join('')}
        
        <!-- Overall length line and label -->
        <line x1="${minX}" y1="90" x2="${maxX}" y2="90" stroke="#bbb" strokeWidth="2" />
        <line x1="${minX}" y1="85" x2="${minX}" y2="95" stroke="#bbb" strokeWidth="2" />
        <line x1="${maxX}" y1="85" x2="${maxX}" y2="95" stroke="#bbb" strokeWidth="2" />
        <rect x="${(minX + maxX) / 2 - 48}" y="65" width="96" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
        <text x="${(minX + maxX) / 2}" y="75" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500" dominantBaseline="middle">
          ${unit === 'ft' ? `A = ${parseFloat(overallLen).toFixed(2).replace(/\.00$/, '')}'` : `A = ${overallLen}"`}
        </text>

        <!-- B length trunk line - positioned on the middle wire -->
        <rect x="${(200 + 750) / 2 - 48}" y="251 - 10" width="96" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
        <text x="${(200 + 750) / 2}" y="251 + 5" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
          ${unit === 'ft' ? `B = ${parseFloat(lengths['B']).toFixed(2).replace(/\.00$/, '')}'` : `B = ${parseFloat(lengths['B']) || 0}"`}
        </text>

        <!-- Length labels with oval outlined boxes - no dimension lines -->
        <rect x="${cLabel.x-32}" y="${cLabel.y-10}" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
        <text x="${cLabel.x}" y="${cLabel.y+5}" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
          ${lengths['C'] ? (unit === 'ft' ? `C = ${parseFloat(lengths['C']).toFixed(2).replace(/\.00$/, '')}'` : `C = ${lengths['C']}"`) : 'C'}
        </text>
        <rect x="${dLabel.x-32}" y="${dLabel.y-10}" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
        <text x="${dLabel.x}" y="${dLabel.y+5}" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
          ${lengths['D'] ? (unit === 'ft' ? `D = ${parseFloat(lengths['D']).toFixed(2).replace(/\.00$/, '')}'` : `D = ${lengths['D']}"`) : 'D'}
        </text>
        <rect x="${eLabel.x-32}" y="${eLabel.y-10}" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
        <text x="${eLabel.x}" y="${eLabel.y+5}" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
          ${lengths['E'] ? (unit === 'ft' ? `E = ${parseFloat(lengths['E']).toFixed(2).replace(/\.00$/, '')}'` : `E = ${lengths['E']}"`) : 'E'}
        </text>
        <rect x="${fLabel.x-32}" y="${fLabel.y-10}" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
        <text x="${fLabel.x}" y="${fLabel.y+5}" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
          ${lengths['F'] ? (unit === 'ft' ? `F = ${parseFloat(lengths['F']).toFixed(2).replace(/\.00$/, '')}'` : `F = ${lengths['F']}"`) : 'F'}
        </text>
        <rect x="${gLabel.x-32}" y="${gLabel.y-10}" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
        <text x="${gLabel.x}" y="${gLabel.y+5}" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
          ${lengths['G'] ? (unit === 'ft' ? `G = ${parseFloat(lengths['G']).toFixed(2).replace(/\.00$/, '')}'` : `G = ${lengths['G']}"`) : 'G'}
        </text>
        <rect x="${hLabel.x-32}" y="${hLabel.y-10}" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
        <text x="${hLabel.x}" y="${hLabel.y+5}" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
          ${lengths['H'] ? (unit === 'ft' ? `H = ${parseFloat(lengths['H']).toFixed(2).replace(/\.00$/, '')}'` : `H = ${lengths['H']}"`) : 'H'}
        </text>
      </g>
    </svg>
  `;
  
  return svgContent;
}

// Generate SVG for regular Hybrid cables (4 connectors: F,G audio, H,I power)
export function generateHybridSVG({
  connectors = {},
  lengths = {},
  unit = 'in',
  svgProps = {},
  asString = false
}) {
  // Use the exact same layout and calculations as HybridConnectorSelection.js
  const pad = 20;
  const connW = 40, connH = 40;
  
  // Calculate lengths
  const aLen = parseFloat(lengths['A']) || 0;
  const cLen = parseFloat(lengths['C']) || 0;
  const dLen = parseFloat(lengths['D']) || 0;
  const eLen = parseFloat(lengths['E']) || 0;
  const fLen = parseFloat(lengths['F']) || 0;
  
  // Visual scaling - exact same as HybridConnectorSelection.js
  const PX_PER_INCH_VISUAL = 6;
  const MAX_VISUAL_LENGTH = 300;
  
  // Calculate visual lengths
  const cVisual = Math.min(cLen * PX_PER_INCH_VISUAL, MAX_VISUAL_LENGTH);
  const dVisual = Math.min(dLen * PX_PER_INCH_VISUAL, MAX_VISUAL_LENGTH);
  const eVisual = Math.min(eLen * PX_PER_INCH_VISUAL, MAX_VISUAL_LENGTH);
  const fVisual = Math.min(fLen * PX_PER_INCH_VISUAL, MAX_VISUAL_LENGTH);
  
  // Use visual lengths for positioning (with minimum for very short cables)
  const branchLenCPx = Math.max(80, cVisual);
  const branchLenDPx = Math.max(80, dVisual);
  const branchLenEPx = Math.max(80, eVisual);
  const branchLenFPx = Math.max(80, fVisual);
  
  // Endpoints - exact same as HybridConnectorSelection.js
  const G_end = { x: 200 - branchLenCPx - 10, y: 160 };
  const H_end = { x: 750 + branchLenDPx + 10, y: 160 };
  const I_end = { x: 200 - branchLenEPx - 10, y: 340 };
  const J_end = { x: 750 + branchLenFPx + 10, y: 340 };
  
  // Zoom/fit logic - exact same as HybridConnectorSelection.js
  const allX = [G_end.x - connW, H_end.x + connW, I_end.x - connW, J_end.x + connW];
  const allY = [G_end.y - connH, H_end.y - connH, I_end.y + connH, J_end.y + connH, 100, 400];
  const minX = Math.min(...allX);
  const maxX = Math.max(...allX);
  const minY = Math.min(...allY);
  const maxY = Math.max(...allY);
  
  // Auto-scaling logic
  const contentWidth = maxX - minX;
  const contentHeight = maxY - minY;
  const viewBoxWidth = contentWidth + 2 * pad;
  const viewBoxHeight = contentHeight + 2 * pad;
  const viewBoxX = minX - pad;
  const viewBoxY = minY - pad;
  
  // Dimension line variables - exact same as HybridConnectorSelection.js
  const leftmostX = Math.min(G_end.x, I_end.x);
  const rightmostX = Math.max(H_end.x, J_end.x);
  const overallLineY = 100;
  
  // Label positions - exact same as HybridConnectorSelection.js
  const cLabel = { x: 200 - branchLenCPx / 2, y: 120 };
  const dLabel = { x: 750 + branchLenDPx / 2, y: 120 };
  const eLabel = { x: 200 - branchLenEPx / 2, y: 380 };
  const fLabel = { x: 750 + branchLenFPx / 2, y: 380 };

  // Generate straight wire path - exact same as HybridConnectorSelection.js
  const generateStraightWire = (x1, x2, y, length) => {
    return `M ${x1} ${y} L ${x2} ${y}`;
  };

  // Get connector images using the same logic as HybridConnectorSelection.js
  const getConnectorImage = (connector, configuration) => {
    if (!connector?.image) return connector?.image;
    
    // Handle XLR connectors (3-pin and 5-pin)
    if (connector.family === 'XLR' || connector.id?.startsWith('X') || connector.id?.startsWith('NC3') || connector.id?.startsWith('NC5')) {
      if (configuration === 'right-angle') {
        if (connector.gender?.toLowerCase() === 'male') {
          return '/images/nc3mrx.jpg';
        }
        if (connector.gender?.toLowerCase() === 'female') {
          return '/images/nc3frx.jpg';
        }
      } else if (configuration === 'panel-mount') {
        if (connector.gender?.toLowerCase() === 'male') {
          return '/images/nc3md-l-1.jpg';
        }
        if (connector.gender?.toLowerCase() === 'female') {
          return '/images/nc3fd-L-1.jpg';
        }
      }
      // Standard XLR - return the original image
      return connector.image;
    }
    
    // Handle 1/4" TS connectors
    if (connector.family === 'TS' || connector.id === '2T_M' || connector.id === '2T_F') {
      if (connector.gender?.toLowerCase() === 'male') {
        if (configuration === 'right-angle') {
          return '/images/1_4_ TS-RA (ACPM-TN).png';
        } else if (configuration === 'panel-mount') {
          return '/images/coming_soon.png'; // Placeholder for panel mount
        } else {
          return '/images/1_4_ TS (ACPM-GN).png'; // Standard
        }
      } else {
        // All female variants use placeholder
        return '/images/coming_soon.png';
      }
    }
    
    // Handle 1/4" TRS connectors
    if (connector.family === 'TRS' || connector.id === '2TR_M' || connector.id === '2TR_F') {
      if (connector.gender?.toLowerCase() === 'male') {
        if (configuration === 'right-angle') {
          return '/images/1_4_ TRS-RA (ACPS-TN).png';
        } else if (configuration === 'panel-mount') {
          return '/images/coming_soon.png'; // Placeholder for panel mount
        } else {
          return '/images/1_4_ TRS (ACPS-GN).png'; // Standard
        }
      } else {
        // All female variants use placeholder
        return '/images/coming_soon.png';
      }
    }
    
    // Handle 3.5mm TS connectors
    if (connector.id === '3T_M' || connector.id === '3T_F') {
      if (connector.gender?.toLowerCase() === 'male') {
        if (configuration === 'right-angle') {
          return '/images/coming_soon.png'; // Placeholder for right angle
        } else if (configuration === 'panel-mount') {
          return '/images/coming_soon.png'; // Placeholder for panel mount
        } else {
          return '/images/TS_3_5mm_male.png'; // Standard
        }
      } else {
        // All female variants use placeholder
        return '/images/coming_soon.png';
      }
    }
    
    // Handle 3.5mm TRS connectors
    if (connector.id === '3TR_M' || connector.id === '3TR_F') {
      if (connector.gender?.toLowerCase() === 'male') {
        if (configuration === 'right-angle') {
          return '/images/coming_soon.png'; // Placeholder for right angle
        } else if (configuration === 'panel-mount') {
          return '/images/coming_soon.png'; // Placeholder for panel mount
        } else {
          return '/images/TRS_3_5mm_male.png'; // Standard
        }
      } else {
        if (configuration === 'right-angle') {
          return '/images/coming_soon.png'; // Placeholder for right angle
        } else if (configuration === 'panel-mount') {
          return '/images/coming_soon.png'; // Placeholder for panel mount
        } else {
          return '/images/TRS_3_5mm_female.png'; // Standard
        }
      }
    }
    
    // Handle RCA connectors
    if (connector.family === 'RCA' || connector.id === 'RCA_M' || connector.id === 'RCA_F') {
      if (configuration === 'right-angle') {
        return '/images/coming_soon.png'; // Placeholder for right angle
      } else if (configuration === 'panel-mount') {
        return '/images/coming_soon.png'; // Placeholder for panel mount
      } else {
        // Standard RCA - return the original image
        return connector.image;
      }
    }
    
    // Handle Speakon connectors (no right-angle variants available)
    if (connector.family === 'Speakon' || connector.id?.startsWith('SPK')) {
      if (configuration === 'right-angle') {
        return '/images/coming_soon.png'; // Placeholder for right angle (not available)
      } else if (configuration === 'panel-mount') {
        return '/images/coming_soon.png'; // Placeholder for panel mount
      } else {
        // Standard Speakon - return the original image
        return connector.image;
      }
    }
    
    // Handle PowerCON connectors
    if (connector.family?.includes('PowerCon') || connector.id?.includes('PowerCon')) {
      if (configuration === 'right-angle') {
        return '/images/coming_soon.png'; // Placeholder for right angle
      } else if (configuration === 'panel-mount') {
        if (connector.family === 'PowerCon_Blue' || connector.id?.includes('Blue')) {
          return '/images/NAC3MPXXA.jpg'; // Blue panel mount male
        } else {
          return '/images/NAC3MPXXB.jpg'; // Grey panel mount female
        }
      } else {
        // Standard configuration
        if (connector.family === 'PowerCon_Blue' || connector.id?.includes('Blue')) {
          return '/images/NAC3FXXA-W-S.png'; // Blue standard female
        } else {
          return '/images/NAC3FXXB-W-S.png'; // Grey standard male
        }
      }
    }
    
    // Handle IEC connectors
    if (connector.family?.includes('IEC') || connector.id?.includes('IEC')) {
      if (configuration === 'right-angle') {
        return '/images/coming_soon.png'; // Placeholder for right angle
      } else if (configuration === 'panel-mount') {
        if (connector.gender?.toLowerCase() === 'male') {
          return '/images/IEC_C14_panel_mount_male.png';
        } else if (connector.gender?.toLowerCase() === 'female') {
          return '/images/IEC_C13_panel_mount_female.png';
        }
      } else {
        // Standard IEC - return the original image
        return connector.image;
      }
    }
    
    // Handle NEMA 5-15 connectors
    if (connector.family?.includes('NEMA') || connector.id?.includes('5-15')) {
      if (configuration === 'right-angle') {
        return '/images/coming_soon.png'; // Placeholder for right angle
      } else if (configuration === 'panel-mount') {
        if (connector.gender?.toLowerCase() === 'male') {
          return '/images/515P_M.jpg';
        } else if (connector.gender?.toLowerCase() === 'female') {
          return '/images/515R_F.jpg';
        }
      } else {
        // Standard NEMA - return the original image
        return connector.image;
      }
    }
    
    // Handle True1 connectors
    if (connector.family?.includes('True1') || connector.id?.includes('True1')) {
      if (configuration === 'right-angle') {
        return '/images/coming_soon.png'; // Placeholder for right angle
      } else if (configuration === 'panel-mount') {
        return '/images/coming_soon.png'; // Placeholder for panel mount
      } else {
        // Standard True1 - return the original image
        return connector.image;
      }
    }
    
    // Default fallback - return the original image
    return connector.image;
  };

  // Get connector images for each point
  const connectorImages = {};
  ['F', 'G', 'H', 'I'].forEach(point => {
    const connector = connectors[point];
    if (connector?.id) {
      connectorImages[point] = getConnectorImage(connector, connector.configuration || 'standard');
    }
  });

  // Generate SVG content - exact same as HybridConnectorSelection.js
  const svgContent = `
    <svg 
      width="100%" 
      viewBox="${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}" 
      preserveAspectRatio="xMidYMid meet"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      style="font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;"
    >
      <!-- Shadow filter - exact same as HybridConnectorSelection.js -->
      <defs>
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="2" dy="3" stdDeviation="3" floodColor="#000" floodOpacity="0.3" />
        </filter>
      </defs>
      
      <g>
        <!-- Central wire (A) - Represents the main cable length - exact same as HybridConnectorSelection.js -->
        <path 
          d="${generateStraightWire(200, 750, 251, parseFloat(lengths['A']) || 6)}" 
          stroke="#666" 
          strokeWidth="8" 
          fill="none" 
          filter="url(#shadow)"
        />
        
        <!-- Cable sheath outline for better visibility - exact same as HybridConnectorSelection.js -->
        <rect x="200" y="245" width="550" height="12" rx="6" fill="#000" stroke="#b32b2b" strokeWidth=".5" opacity="0.7" />
        
        <!-- Audio branches (G, H) - Lighter blue, thin with prettier curves - exact same as HybridConnectorSelection.js -->
        <path d="M 200 251 C ${200 - 30} 230 ${200 - cVisual * 0.7} 190 ${200 - cVisual - 10} 160 C ${200 - cVisual - 25} 145 ${G_end.x + 15} ${G_end.y + 15} ${G_end.x} ${G_end.y}" stroke="#6495c8" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
        <path d="M 750 251 C ${750 + 30} 230 ${750 + dVisual * 0.7} 190 ${750 + dVisual + 10} 160 C ${750 + dVisual + 25} 145 ${H_end.x - 15} ${H_end.y + 15} ${H_end.x} ${H_end.y}" stroke="#6495c8" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
        
        <!-- Power branches (I, J) - Black, thick with prettier curves - exact same as HybridConnectorSelection.js -->
        <path d="M 200 251 C ${200 - 30} 270 ${200 - eVisual * 0.7} 310 ${200 - eVisual - 10} 340 C ${200 - eVisual - 25} 355 ${I_end.x + 15} ${I_end.y - 15} ${I_end.x} ${I_end.y}" stroke="#333333" strokeWidth="10" fill="none" filter="url(#shadow)" strokeLinecap="round" />
        <path d="M 750 251 C ${750 + 30} 270 ${750 + fVisual * 0.7} 310 ${750 + fVisual + 10} 340 C ${750 + fVisual + 25} 355 ${J_end.x - 15} ${J_end.y - 15} ${J_end.x} ${J_end.y}" stroke="#333333" strokeWidth="10" fill="none" filter="url(#shadow)" strokeLinecap="round" />

        <!-- Connector points - exact same as HybridConnectorSelection.js -->
        ${['G', 'H', 'I', 'J'].map(point => {
          const endPos = point === 'G' ? G_end :
                        point === 'H' ? H_end :
                        point === 'I' ? I_end :
                        J_end;
          const color = point === 'G' || point === 'H' ? '#6495c8' : '#333333';
          const connector = connectors[point];
          
          if (connector?.image) {
            return `
              <image 
                href="${connectorImages[point] || connector.image}"
                x="${endPos.x - connW / 2}" 
                y="${endPos.y - connH / 2}" 
                width="${connW}" 
                height="${connH}" 
                filter="url(#shadow)"
              />
            `;
          } else {
            return `
              <rect 
                x="${endPos.x - connW / 2}" 
                y="${endPos.y - connH / 2}" 
                width="${connW}" 
                height="${connH}" 
                rx="6" 
                fill="${color}" 
                stroke="#fff" 
                strokeWidth="3" 
                filter="url(#shadow)"
              />
            `;
          }
        }).join('')}

        <!-- Connector labels - exact same as HybridConnectorSelection.js -->
        ${['G', 'H', 'I', 'J'].map(point => {
          const endPos = point === 'G' ? G_end :
                        point === 'H' ? H_end :
                        point === 'I' ? I_end :
                        J_end;
          return `
            <text 
              x="${endPos.x}" 
              y="${endPos.y + connH / 2 + 20}" 
              textAnchor="middle" 
              fontSize="14" 
              fill="#000" 
              fontWeight="bold"
            >
              ${point}
            </text>
          `;
        }).join('')}

        <!-- Overall length line and label - exact same as HybridConnectorSelection.js -->
        <line x1="${leftmostX}" y1="${overallLineY}" x2="${rightmostX}" y2="${overallLineY}" stroke="#bbb" strokeWidth="2" />
        <line x1="${leftmostX}" y1="${overallLineY-5}" x2="${leftmostX}" y2="${overallLineY+5}" stroke="#bbb" strokeWidth="2" />
        <line x1="${rightmostX}" y1="${overallLineY-5}" x2="${rightmostX}" y2="${overallLineY+5}" stroke="#bbb" strokeWidth="2" />
        <rect x="${(leftmostX + rightmostX) / 2 - 48}" y="${overallLineY - 25}" width="96" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
        <text x="${(leftmostX + rightmostX) / 2}" y="${overallLineY - 15}" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500" dominantBaseline="middle">
          ${unit === 'ft' ? `A = ${parseFloat(aLen).toFixed(2).replace(/\.00$/, '')}'` : `A = ${aLen}"`}
        </text>

        <!-- B length trunk line - positioned on the middle wire - exact same as HybridConnectorSelection.js -->
        <rect x="${(200 + 750) / 2 - 48}" y="251 - 10" width="96" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
        <text x="${(200 + 750) / 2}" y="251 + 5" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
          ${unit === 'ft' ? `B = ${parseFloat(lengths['B']).toFixed(2).replace(/\.00$/, '')}'` : `B = ${parseFloat(lengths['B']) || 0}"`}
        </text>
        
        <!-- Length labels with oval outlined boxes - no dimension lines - exact same as HybridConnectorSelection.js -->
        <rect x="${cLabel.x-32}" y="${cLabel.y-10}" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
        <text x="${cLabel.x}" y="${cLabel.y+5}" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
          ${lengths['C'] ? (unit === 'ft' ? `C = ${parseFloat(lengths['C']).toFixed(2).replace(/\.00$/, '')}'` : `C = ${lengths['C']}"`) : 'C'}
        </text>
        <rect x="${dLabel.x-32}" y="${dLabel.y-10}" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
        <text x="${dLabel.x}" y="${dLabel.y+5}" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
          ${lengths['D'] ? (unit === 'ft' ? `D = ${parseFloat(lengths['D']).toFixed(2).replace(/\.00$/, '')}'` : `D = ${lengths['D']}"`) : 'D'}
        </text>
        <rect x="${eLabel.x-32}" y="${eLabel.y-10}" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
        <text x="${eLabel.x}" y="${eLabel.y+5}" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
          ${lengths['E'] ? (unit === 'ft' ? `E = ${parseFloat(lengths['E']).toFixed(2).replace(/\.00$/, '')}'` : `E = ${lengths['E']}"`) : 'E'}
        </text>
        <rect x="${fLabel.x-32}" y="${fLabel.y-10}" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
        <text x="${fLabel.x}" y="${fLabel.y+5}" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
          ${lengths['F'] ? (unit === 'ft' ? `F = ${parseFloat(lengths['F']).toFixed(2).replace(/\.00$/, '')}'` : `F = ${lengths['F']}"`) : 'F'}
        </text>
      </g>
    </svg>
  `;

  if (asString) {
    return svgContent;
  }

  return {
    svg: svgContent,
    width: viewBoxWidth,
    height: viewBoxHeight,
    viewBox: `${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}`
  };
}

// Generate SVG for Wye cables (3 connectors: D,E,F with Y-shaped layout)
export function generateWyeSVG({
  connectors = {},
  lengths = {},
  unit = 'in',
  connectorType = 'audio',
  svgProps = {},
  asString = false
}) {
  // Use the exact same layout and calculations as WyeConnectorSelection.js
  const svgWidth = 950, pad = 20;
  const connW = 40, connH = 40;
  const splitY = 200;
  const branchAngle = 35;
  const aLen = parseFloat(lengths['A']) || 0; // Overall length
  const bLen = parseFloat(lengths['B']) || 0; // D to split
  const cLen = parseFloat(lengths['C']) || 0; // split to E
  const dLen = parseFloat(lengths['D']) || 0; // split to F
  
  // Visual scaling for demo (like hybrid components)
  const PX_PER_INCH_VISUAL = 6; // Consistent with other components
  
  // Maximum visual length for SVG display (equivalent to ~50 inches visually)
  const MAX_VISUAL_LENGTH = 300;
  
  // Calculate visual lengths with consistent scaling
  const bLenPx = bLen * PX_PER_INCH_VISUAL;
  const cLenPx = cLen * PX_PER_INCH_VISUAL;
  const dLenPx = dLen * PX_PER_INCH_VISUAL;
  
  // Apply visual length caps for reasonable SVG display
  const bVisual = Math.min(bLenPx, MAX_VISUAL_LENGTH);
  const cVisual = Math.min(cLenPx, MAX_VISUAL_LENGTH);
  const dVisual = Math.min(dLenPx, MAX_VISUAL_LENGTH);
  
  // Use visual lengths for positioning (with minimum for very short cables)
  const mainLenPx = Math.max(240, bVisual);
  const branchLenCPx = Math.max(80, cVisual);
  const branchLenDPx = Math.max(80, dVisual);
  
  // Endpoints - exact same as WyeConnectorSelection.js
  const leftX = svgWidth * 0.4; // Better centering - position split point closer to center
  const E = { x: leftX - mainLenPx, y: splitY };
  const split = { x: leftX, y: splitY };
  const F = { x: leftX + branchLenCPx * Math.cos(branchAngle * Math.PI / 180), y: splitY - branchLenCPx * Math.sin(branchAngle * Math.PI / 180) };
  const G = { x: leftX + branchLenDPx * Math.cos(branchAngle * Math.PI / 180), y: splitY + branchLenDPx * Math.sin(branchAngle * Math.PI / 180) };
  
  // Zoom/fit logic - exact same as WyeConnectorSelection.js
  const allX = [E.x - connW, split.x, F.x + connW, G.x + connW];
  const allY = [
    E.y - connH, 
    split.y, 
    F.y - connH, 
    G.y + connH,
    Math.min(F.y, G.y) - 90, // Overall length label (updated position)
    G.y + 70 // Bottom G label
  ];
  const minX = Math.min(...allX);
  const maxX = Math.max(...allX);
  const minY = Math.min(...allY);
  const maxY = Math.max(...allY);
  
  // Auto-scaling logic - simplified for better centering
  const contentWidth = maxX - minX;
  const contentHeight = maxY - minY;
  
  // Calculate viewBox for proper centering with adequate padding for labels
  const viewBoxWidth = contentWidth + 2 * pad;
  const viewBoxHeight = contentHeight + 2 * pad; // More padding for labels
  const viewBoxX = minX - pad;
  const viewBoxY = minY - pad; // Full padding at top for A label
  
  // Dimension line variables for professional styling - exact same as WyeConnectorSelection.js
  const leftmostX = Math.min(E.x, F.x, G.x);
  const rightmostX = Math.max(E.x, F.x, G.x);
  const overallLineY = Math.min(F.y, G.y) - 70;
  
  // Midpoint calculations for dimension labels
  const bMid = { x: (E.x + split.x) / 2, y: (E.y + split.y) / 2 - 30 };
  const cMid = { x: (split.x + F.x) / 2, y: (split.y + F.y) / 2 - 20 };
  const dMid = { x: (split.x + G.x) / 2, y: (split.y + G.y) / 2 + 40 };

  // Generate straight wire path - exact same as WyeConnectorSelection.js
  const generateStraightWire = (startX, endX, centerY, cableLength) => {
    // Simple straight line from start to end
    return `M ${startX} ${centerY} L ${endX} ${centerY}`;
  };

  // Get connector images using the same logic as WyeConnectorSelection.js
  const getConnectorImage = (connector, configuration) => {
    if (!connector?.image) return connector?.image;
    
    // Handle XLR connectors (3-pin and 5-pin)
    if (connector.family === 'XLR' || connector.id?.startsWith('X') || connector.id?.startsWith('NC3') || connector.id?.startsWith('NC5')) {
      if (configuration === 'right-angle') {
        if (connector.gender?.toLowerCase() === 'male') {
          return '/images/nc3mrx.jpg';
        }
        if (connector.gender?.toLowerCase() === 'female') {
          return '/images/nc3frx.jpg';
        }
      } else if (configuration === 'panel-mount') {
        if (connector.gender?.toLowerCase() === 'male') {
          return '/images/nc3md-l-1.jpg';
        }
        if (connector.gender?.toLowerCase() === 'female') {
          return '/images/nc3fd-L-1.jpg';
        }
      }
      // Standard XLR - return the original image
      return connector.image;
    }
    
    // Handle 1/4" TS connectors
    if (connector.family === 'TS' || connector.id === '2T_M' || connector.id === '2T_F') {
      if (connector.gender?.toLowerCase() === 'male') {
        if (configuration === 'right-angle') {
          return '/images/1_4_ TS-RA (ACPM-TN).png';
        } else if (configuration === 'panel-mount') {
          return '/images/coming_soon.png'; // Placeholder for panel mount
        } else {
          return '/images/1_4_ TS (ACPM-GN).png'; // Standard
        }
      } else {
        // All female variants use placeholder
        return '/images/coming_soon.png';
      }
    }
    
    // Handle 1/4" TRS connectors
    if (connector.family === 'TRS' || connector.id === '2TR_M' || connector.id === '2TR_F') {
      if (connector.gender?.toLowerCase() === 'male') {
        if (configuration === 'right-angle') {
          return '/images/1_4_ TRS-RA (ACPS-TN).png';
        } else if (configuration === 'panel-mount') {
          return '/images/coming_soon.png'; // Placeholder for panel mount
        } else {
          return '/images/1_4_ TRS (ACPS-GN).png'; // Standard
        }
      } else {
        // All female variants use placeholder
        return '/images/coming_soon.png';
      }
    }
    
    // Handle 3.5mm TS connectors
    if (connector.id === '3T_M' || connector.id === '3T_F') {
      if (connector.gender?.toLowerCase() === 'male') {
        if (configuration === 'right-angle') {
          return '/images/coming_soon.png'; // Placeholder for right angle
        } else if (configuration === 'panel-mount') {
          return '/images/coming_soon.png'; // Placeholder for panel mount
        } else {
          return '/images/TS_3_5mm_male.png'; // Standard
        }
      } else {
        // All female variants use placeholder
        return '/images/coming_soon.png';
      }
    }
    
    // Handle 3.5mm TRS connectors
    if (connector.id === '3TR_M' || connector.id === '3TR_F') {
      if (connector.gender?.toLowerCase() === 'male') {
        if (configuration === 'right-angle') {
          return '/images/coming_soon.png'; // Placeholder for right angle
        } else if (configuration === 'panel-mount') {
          return '/images/coming_soon.png'; // Placeholder for panel mount
        } else {
          return '/images/TRS_3_5mm_male.png'; // Standard
        }
      } else {
        if (configuration === 'right-angle') {
          return '/images/coming_soon.png'; // Placeholder for right angle
        } else if (configuration === 'panel-mount') {
          return '/images/coming_soon.png'; // Placeholder for panel mount
        } else {
          return '/images/TRS_3_5mm_female.png'; // Standard
        }
      }
    }
    
    // Handle RCA connectors
    if (connector.family === 'RCA' || connector.id === 'RCA_M' || connector.id === 'RCA_F') {
      if (configuration === 'right-angle') {
        return '/images/coming_soon.png'; // Placeholder for right angle
      } else if (configuration === 'panel-mount') {
        return '/images/coming_soon.png'; // Placeholder for panel mount
      } else {
        // Standard RCA - return the original image
        return connector.image;
      }
    }
    
    // Handle Speakon connectors (no right-angle variants available)
    if (connector.family === 'Speakon' || connector.id?.startsWith('SPK')) {
      if (configuration === 'right-angle') {
        return '/images/coming_soon.png'; // Placeholder for right angle (not available)
      } else if (configuration === 'panel-mount') {
        return '/images/coming_soon.png'; // Placeholder for panel mount
      } else {
        // Standard Speakon - return the original image
        return connector.image;
      }
    }
    
    // Handle PowerCON connectors
    if (connector.family?.includes('PowerCon') || connector.id?.includes('PowerCon')) {
      if (configuration === 'right-angle') {
        return '/images/coming_soon.png'; // Placeholder for right angle
      } else if (configuration === 'panel-mount') {
        if (connector.family === 'PowerCon_Blue' || connector.id?.includes('Blue')) {
          return '/images/NAC3MPXXA.jpg'; // Blue panel mount male
        } else {
          return '/images/NAC3MPXXB.jpg'; // Grey panel mount female
        }
      } else {
        // Standard configuration
        if (connector.family === 'PowerCon_Blue' || connector.id?.includes('Blue')) {
          return '/images/NAC3FXXA-W-S.png'; // Blue standard female
        } else {
          return '/images/NAC3FXXB-W-S.png'; // Grey standard male
        }
      }
    }
    
    // Handle IEC connectors
    if (connector.family?.includes('IEC') || connector.id?.includes('IEC')) {
      if (configuration === 'right-angle') {
        return '/images/coming_soon.png'; // Placeholder for right angle
      } else if (configuration === 'panel-mount') {
        if (connector.gender?.toLowerCase() === 'male') {
          return '/images/IEC_C14_panel_mount_male.png';
        } else if (connector.gender?.toLowerCase() === 'female') {
          return '/images/IEC_C13_panel_mount_female.png';
        }
      } else {
        // Standard IEC - return the original image
        return connector.image;
      }
    }
    
    // Handle NEMA 5-15 connectors
    if (connector.family?.includes('NEMA') || connector.id?.includes('5-15')) {
      if (configuration === 'right-angle') {
        return '/images/coming_soon.png'; // Placeholder for right angle
      } else if (configuration === 'panel-mount') {
        if (connector.gender?.toLowerCase() === 'male') {
          return '/images/515P_M.jpg';
        } else if (connector.gender?.toLowerCase() === 'female') {
          return '/images/515R_F.jpg';
        }
      } else {
        // Standard NEMA - return the original image
        return connector.image;
      }
    }
    
    // Handle True1 connectors
    if (connector.family?.includes('True1') || connector.id?.includes('True1')) {
      if (configuration === 'right-angle') {
        return '/images/coming_soon.png'; // Placeholder for right angle
      } else if (configuration === 'panel-mount') {
        return '/images/coming_soon.png'; // Placeholder for panel mount
      } else {
        // Standard True1 - return the original image
        return connector.image;
      }
    }
    
    // Default fallback - return the original image
    return connector.image;
  };

  // Get connector images for each point
  const connectorImages = {};
  ['E', 'F', 'G'].forEach(point => {
    const connector = connectors[point];
    if (connector?.id) {
      connectorImages[point] = getConnectorImage(connector, connector.configuration || 'standard');
    }
  });

  // Generate SVG content - exact same as WyeConnectorSelection.js
  const svgContent = `
    <svg 
      width="100%" 
      viewBox="${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}" 
      preserveAspectRatio="xMidYMid meet"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      style="font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;"
    >
      <!-- Shadow filter - exact same as WyeConnectorSelection.js -->
      <defs>
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="2" dy="3" stdDeviation="3" floodColor="#000" floodOpacity="0.3" />
        </filter>
      </defs>
      
      <g>
        <!-- Main cable - Represents the main cable length - exact same as WyeConnectorSelection.js -->
        <path 
          d="${generateStraightWire(E.x, E.x + mainLenPx, E.y, parseFloat(lengths['B']) || 6)}" 
          stroke="#6495c8" 
          strokeWidth="10" 
          fill="none" 
          filter="url(#shadow)"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.9"
        />
        <!-- Add a second path with thicker stroke to show cable bulk - exact same as WyeConnectorSelection.js -->
        <path 
          d="${generateStraightWire(E.x, E.x + mainLenPx, E.y, parseFloat(lengths['B']) || 6)}" 
          stroke="#6495c8" 
          strokeWidth="8" 
          fill="none" 
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="1"
        />
        
        <!-- Cable sheath outline for better visibility - exact same as WyeConnectorSelection.js -->
        <rect 
          x="${E.x}" 
          y="${E.y - 6}" 
          width="${mainLenPx}" 
          height="12" 
          rx="6" 
          fill="none" 
          stroke="#6495c8" 
          strokeWidth="1" 
          opacity="0" 
        />
        
        <!-- Branch cables with lighter colors - exact same as WyeConnectorSelection.js -->
        <path 
          d="M ${split.x} ${split.y} L ${F.x} ${F.y}" 
          stroke="${connectorType === 'power' ? '#333333' : '#6495c8'}" 
          strokeWidth="${connectorType === 'power' ? '10' : '6'}" 
          fill="none" 
          filter="url(#shadow)" 
        />
        <path 
          d="M ${split.x} ${split.y} L ${G.x} ${G.y}" 
          stroke="${connectorType === 'power' ? '#333333' : '#6495c8'}" 
          strokeWidth="${connectorType === 'power' ? '10' : '6'}" 
          fill="none" 
          filter="url(#shadow)" 
        />

        <!-- Connector boxes - exact same as WyeConnectorSelection.js -->
        ${['E', 'F', 'G'].map(point => {
          const pos = point === 'E' ? E : point === 'F' ? F : G;
          const color = connectorType === 'power' ? '#333333' : '#6495c8';
          const connector = connectors[point];
          
          if (connector?.image) {
            return `
              <image 
                href="${connectorImages[point] || connector.image}"
                x="${pos.x - connW / 2}" 
                y="${pos.y - connH / 2}" 
                width="${connW}" 
                height="${connH}" 
                filter="url(#shadow)"
              />
            `;
          } else {
            return `
              <rect 
                x="${pos.x - connW / 2}" 
                y="${pos.y - connH / 2}" 
                width="${connW}" 
                height="${connH}" 
                rx="6" 
                fill="${color}" 
                stroke="#fff" 
                strokeWidth="3" 
                filter="url(#shadow)"
              />
            `;
          }
        }).join('')}

        <!-- Connector labels - exact same as WyeConnectorSelection.js -->
        ${['E', 'F', 'G'].map(point => {
          const pos = point === 'E' ? E : point === 'F' ? F : G;
          return `
            <text 
              x="${pos.x}" 
              y="${pos.y + connH / 2 + 20}" 
              textAnchor="middle" 
              fontSize="14" 
              fill="#000" 
              fontWeight="bold"
            >
              ${point}
            </text>
          `;
        }).join('')}

        <!-- Dimension lines and labels - Professional style - exact same as WyeConnectorSelection.js -->
        <line x1="${leftmostX}" y1="${overallLineY}" x2="${rightmostX}" y2="${overallLineY}" stroke="#bbb" strokeWidth="2" />
        <line x1="${leftmostX}" y1="${overallLineY-5}" x2="${leftmostX}" y2="${overallLineY+5}" stroke="#bbb" strokeWidth="2" />
        <line x1="${rightmostX}" y1="${overallLineY-5}" x2="${rightmostX}" y2="${overallLineY+5}" stroke="#bbb" strokeWidth="2" />
        <rect x="${(leftmostX + rightmostX) / 2 - 48}" y="${overallLineY - 15}" width="96" height="30" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
        <text x="${(leftmostX + rightmostX) / 2}" y="${overallLineY}" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500" dominantBaseline="middle">
          ${unit === 'ft' ? `A = ${parseFloat(aLen).toFixed(2).replace(/\.00$/, '')}'` : `A = ${aLen}"`}
        </text>
        
        <!-- Additional dimension lines - exact same as WyeConnectorSelection.js -->
        <rect x="${bMid.x-32}" y="${bMid.y+20}" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
        <text x="${bMid.x}" y="${bMid.y+35}" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
          ${lengths['B'] ? (unit === 'ft' ? `B = ${parseFloat(lengths['B']).toFixed(2).replace(/\.00$/, '')}'` : `B = ${lengths['B']}"`) : 'B'}
        </text>
        <rect x="${cMid.x-52}" y="${cMid.y+10}" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
        <text x="${cMid.x-20}" y="${cMid.y+25}" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
          ${lengths['C'] ? (unit === 'ft' ? `C = ${parseFloat(lengths['C']).toFixed(2).replace(/\.00$/, '')}'` : `C = ${lengths['C']}"`) : 'C'}
        </text>
        <rect x="${dMid.x-52}" y="${dMid.y-50}" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
        <text x="${dMid.x-20}" y="${dMid.y-35}" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
          ${lengths['D'] ? (unit === 'ft' ? `D = ${parseFloat(lengths['D']).toFixed(2).replace(/\.00$/, '')}'` : `D = ${lengths['D']}"`) : 'D'}
        </text>
      </g>
    </svg>
  `;

  if (asString) {
    return svgContent;
  }

  return {
    svg: svgContent,
    width: viewBoxWidth,
    height: viewBoxHeight,
    viewBox: `${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}`
  };
}

// Generate SVG for Zip Audio + Audio cables (4 connectors: A,B,C,D with parallel audio cables)
export function generateZipAudioAudioSVG({
  connectors = {},
  lengths = {},
  unit = 'in',
  svgProps = {},
  asString = false
}) {
  // Calculate visual lengths with consistent scaling
  const cLen = PX_PER_INCH * (parseFloat(lengths['C']) || 0);
  const dLen = PX_PER_INCH * (parseFloat(lengths['D']) || 0);
  const eLen = PX_PER_INCH * (parseFloat(lengths['E']) || 0);
  const fLen = PX_PER_INCH * (parseFloat(lengths['F']) || 0);
  
  // Apply visual length caps for reasonable SVG display
  const cVisual = Math.min(cLen, MAX_VISUAL_LENGTH);
  const dVisual = Math.min(dLen, MAX_VISUAL_LENGTH);
  const eVisual = Math.min(eLen, MAX_VISUAL_LENGTH);
  const fVisual = Math.min(fLen, MAX_VISUAL_LENGTH);
  
  // For very short lengths, make the branches visually even shorter
  const minBranch = 3; // visually minimum px for short lengths
  const cShort = Math.max(cVisual, minBranch);
  const dShort = Math.max(dVisual, minBranch);
  const eShort = Math.max(eVisual, minBranch);
  const fShort = Math.max(fVisual, minBranch);
  
  // Calculate endpoint positions using the same approach as ZipAudioAudioConnectorSelection
  const G_end_adj = { x: 200 - cShort - 50, y: 80 };     // G uses C length (left, top)
  const H_end_adj = { x: 750 + dShort + 50, y: 80 };     // H uses D length (right, top)
  const I_end_adj = { x: 200 - eShort - 50, y: 320 };    // I uses E length (left, bottom)
  const J_end_adj = { x: 750 + fShort + 50, y: 320 };    // J uses F length (right, bottom)
  
  // Calculate overall line positions using the same logic as ZipAudioAudioConnectorSelection
  const overallLineY = 40;
  const overallLabelY = 25;
  
  // Calculate dynamic junction points for better centering
  const centerX = (Math.min(G_end_adj.x, I_end_adj.x) + Math.max(H_end_adj.x, J_end_adj.x)) / 2;
  const junctionSpacing = 450; // Fixed spacing between junctions for consistent main cable length
  const leftJunction = { x: centerX - junctionSpacing / 2, y: 80 };
  const rightJunction = { x: centerX + junctionSpacing / 2, y: 80 };
  
  // For zoom-out, include the outermost edges of connector boxes
  const extraPad = 10;
  const allX = [
    G_end_adj.x - extraPad, // left edge of G
    I_end_adj.x - extraPad, // left edge of I
    H_end_adj.x + connW + extraPad, // right edge of H
    J_end_adj.x + connW + extraPad, // right edge of J
    leftJunction.x, rightJunction.x  // Dynamic junction points
  ];
  
  const minX = Math.min(...allX);
  const maxX = Math.max(...allX);
  
  // Auto-scaling logic - simplified for better centering
  const pad = 40;
  const contentWidth = maxX - minX;
  
  // Calculate actual content bounds including labels - ensure A label isn't cut off
  const minY = Math.min(G_end_adj.y, H_end_adj.y, overallLabelY) - 40 - 15; // Include overall label
  const maxY = Math.max(I_end_adj.y, J_end_adj.y) + 40 + 15; // Bottom connectors + label
  const contentHeight = maxY - minY;
  
  // Calculate viewBox for proper centering with adequate padding for labels
  const viewBoxWidth = contentWidth + 2 * pad;
  const viewBoxHeight = contentHeight + 2 * pad; // More padding for labels
  const viewBoxX = minX - pad - 20; // Slight shift left to center better in viewport
  const viewBoxY = minY - pad; // Full padding at top for A label
  
  // Dynamic label positions - positioned along each wire path using visual lengths
  const cMid = { x: leftJunction.x - (cVisual * 0.6), y: leftJunction.y - (cVisual * 0.3) }; // Along C wire path (G)
  const dMid = { x: rightJunction.x + (dVisual * 0.6), y: rightJunction.y - (dVisual * 0.3) }; // Along D wire path (H)
  const eMid = { x: leftJunction.x - (eVisual * 0.6), y: leftJunction.y + 100 + (eVisual * 0.3) }; // Along E wire path (I)
  const fMid = { x: rightJunction.x + (fVisual * 0.6), y: rightJunction.y + 100 + (fVisual * 0.3) }; // Along F wire path (J)
  
  // Calculate the true leftmost and rightmost endpoints for overall length
  const leftmostX = Math.min(G_end_adj.x, I_end_adj.x);
  const rightmostX = Math.max(H_end_adj.x, J_end_adj.x);
  
  const overallLen = parseFloat(lengths['A']) || 0;
  
  // Helper function to get connector image based on configuration (copied from ZipAudioAudioConnectorSelection)
  const getConnectorImage = (connector, configuration) => {
    if (!connector?.image) return connector?.image;
    
    // Handle XLR connectors (3-pin and 5-pin)
    if (connector.family === 'XLR' || connector.id?.startsWith('X') || connector.id?.startsWith('NC3') || connector.id?.startsWith('NC5')) {
      if (configuration === 'right-angle') {
        if (connector.gender?.toLowerCase() === 'male') {
          return '/images/nc3mrx.jpg';
        }
        if (connector.gender?.toLowerCase() === 'female') {
          return '/images/nc3frx.jpg';
        }
      } else if (configuration === 'panel-mount') {
        if (connector.gender?.toLowerCase() === 'male') {
          return '/images/nc3md-l-1.jpg';
        }
        if (connector.gender?.toLowerCase() === 'female') {
          return '/images/nc3fd-L-1.jpg';
        }
      }
      // Standard XLR - return the original image
      return connector.image;
    }
    
    // Handle 1/4" TS connectors
    if (connector.family === 'TS' || connector.id === '2T_M' || connector.id === '2T_F') {
      if (connector.gender?.toLowerCase() === 'male') {
        if (configuration === 'right-angle') {
          return '/images/1_4_ TS-RA (ACPM-TN).png';
        } else if (configuration === 'panel-mount') {
          return '/images/coming_soon.png'; // Placeholder for panel mount
        } else {
          return '/images/1_4_ TS (ACPM-GN).png'; // Standard
        }
      } else {
        // All female variants use placeholder
        return '/images/coming_soon.png';
      }
    }
    
    // Handle 1/4" TRS connectors
    if (connector.family === 'TRS' || connector.id === '2TR_M' || connector.id === '2TR_F') {
      if (connector.gender?.toLowerCase() === 'male') {
        if (configuration === 'right-angle') {
          return '/images/1_4_ TRS-RA (ACPS-TN).png';
        } else if (configuration === 'panel-mount') {
          return '/images/coming_soon.png'; // Placeholder for panel mount
        } else {
          return '/images/1_4_ TRS (ACPS-GN).png'; // Standard
        }
      } else {
        // All female variants use placeholder
        return '/images/coming_soon.png';
      }
    }
    
    // Handle 3.5mm TS connectors
    if (connector.id === '3T_M' || connector.id === '3T_F') {
      if (connector.gender?.toLowerCase() === 'male') {
        if (configuration === 'right-angle') {
          return '/images/coming_soon.png'; // Placeholder for right angle
        } else if (configuration === 'panel-mount') {
          return '/images/coming_soon.png'; // Placeholder for panel mount
        } else {
          return '/images/TS_3_5mm_male.png'; // Standard
        }
      } else {
        // All female variants use placeholder
        return '/images/coming_soon.png';
      }
    }
    
    // Handle 3.5mm TRS connectors
    if (connector.id === '3TR_M' || connector.id === '3TR_F') {
      if (connector.gender?.toLowerCase() === 'male') {
        if (configuration === 'right-angle') {
          return '/images/coming_soon.png'; // Placeholder for right angle
        } else if (configuration === 'panel-mount') {
          return '/images/coming_soon.png'; // Placeholder for panel mount
        } else {
          return '/images/TRS_3_5mm_male.png'; // Standard
        }
      } else {
        if (configuration === 'right-angle') {
          return '/images/coming_soon.png'; // Placeholder for right angle
        } else if (configuration === 'panel-mount') {
          return '/images/coming_soon.png'; // Placeholder for panel mount
        } else {
          return '/images/TRS_3_5mm_female.png'; // Standard
        }
      }
    }
    
    // Handle RCA connectors
    if (connector.family === 'RCA' || connector.id === 'RCA_M' || connector.id === 'RCA_F') {
      if (configuration === 'right-angle') {
        return '/images/coming_soon.png'; // Placeholder for right angle
      } else if (configuration === 'panel-mount') {
        return '/images/coming_soon.png'; // Placeholder for panel mount
      } else {
        // Standard RCA - return the original image
        return connector.image;
      }
    }
    
    // Handle Speakon connectors (no right-angle variants available)
    if (connector.family === 'Speakon' || connector.id?.startsWith('SPK')) {
      if (configuration === 'right-angle') {
        return '/images/coming_soon.png'; // Placeholder for right angle (not available)
      } else if (configuration === 'panel-mount') {
        return '/images/coming_soon.png'; // Placeholder for panel mount
      } else {
        // Standard Speakon - return the original image
        return connector.image;
      }
    }
    
    // Handle PowerCON connectors
    if (connector.family?.includes('PowerCon') || connector.id?.includes('PowerCon')) {
      if (configuration === 'right-angle') {
        return '/images/coming_soon.png'; // Placeholder for right angle
      } else if (configuration === 'panel-mount') {
        if (connector.family === 'PowerCon_Blue' || connector.id?.includes('Blue')) {
          return '/images/NAC3MPXXA.jpg'; // Blue panel mount male
        } else {
          return '/images/NAC3MPXXB.jpg'; // Grey panel mount female
        }
      } else {
        // Standard configuration
        if (connector.family === 'PowerCon_Blue' || connector.id?.includes('Blue')) {
          return '/images/NAC3FXXA-W-S.png'; // Blue standard female
        } else {
          return '/images/NAC3FXXB-W-S.png'; // Grey standard male
        }
      }
    }
    
    // Handle IEC connectors
    if (connector.family?.includes('IEC') || connector.id?.includes('IEC')) {
      if (configuration === 'right-angle') {
        return '/images/coming_soon.png'; // Placeholder for right angle
      } else if (configuration === 'panel-mount') {
        if (connector.gender?.toLowerCase() === 'male') {
          return '/images/IEC_C14_panel_mount_male.png';
        } else if (connector.gender?.toLowerCase() === 'female') {
          return '/images/IEC_C13_panel_mount_female.png';
        }
      } else {
        // Standard IEC - return the original image
        return connector.image;
      }
    }
    
    // Handle NEMA 5-15 connectors
    if (connector.family?.includes('NEMA') || connector.id?.includes('5-15')) {
      if (configuration === 'right-angle') {
        return '/images/coming_soon.png'; // Placeholder for right angle
      } else if (configuration === 'panel-mount') {
        if (connector.gender?.toLowerCase() === 'male') {
          return '/images/515P_M.jpg';
        } else if (connector.gender?.toLowerCase() === 'female') {
          return '/images/515R_F.jpg';
        }
      } else {
        // Standard NEMA - return the original image
        return connector.image;
      }
    }
    
    // Handle True1 connectors
    if (connector.family?.includes('True1') || connector.id?.includes('True1')) {
      if (configuration === 'right-angle') {
        return '/images/coming_soon.png'; // Placeholder for right angle
      } else if (configuration === 'panel-mount') {
        return '/images/coming_soon.png'; // Placeholder for panel mount
      } else {
        // Standard True1 - return the original image
        return connector.image;
      }
    }
    
    // Default fallback - return the original image
    return connector.image;
  };
  
  // Generate the SVG content
  const svgContent = `
    <svg width="1000" height="500" viewBox="${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}" xmlns="http://www.w3.org/2000/svg" style="font-family: Arial, sans-serif;">
      <!-- Shadow filter -->
      <defs>
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="2" dy="3" stdDeviation="3" floodColor="#000" floodOpacity="0.3" />
        </filter>
      </defs>
      
      <g>
        <!-- B length trunk line - Combined main cable -->
        <path 
          d="M ${leftJunction.x} 206 L ${rightJunction.x} 206"
          stroke="#555" 
          strokeWidth="12" 
          fill="none" 
          strokeLinecap="round"
        />
        
        <!-- Cable sheath outline for better visibility -->
        <rect x="${leftJunction.x}" y="200" width="${rightJunction.x - leftJunction.x}" height="12" rx="6" fill="#000" stroke="#b32b2b" strokeWidth=".5" opacity="0.7" />
        
        <!-- Wire branches - Color-coded for audio pairs with prettier curves -->
        <!-- G and H are paired - both use audio1 - Lighter blue color -->
        <path d="M ${leftJunction.x} 201 C ${leftJunction.x - 30} 180 ${leftJunction.x - cVisual * 0.7} 140 ${leftJunction.x - cVisual - 10} 110 C ${leftJunction.x - cVisual - 25} 95 ${G_end_adj.x + 15} ${G_end_adj.y + 15} ${G_end_adj.x} ${G_end_adj.y}" stroke="#6495c8" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
        <path d="M ${rightJunction.x} 201 C ${rightJunction.x + 30} 180 ${rightJunction.x + dVisual * 0.7} 140 ${rightJunction.x + dVisual + 10} 110 C ${rightJunction.x + dVisual + 25} 95 ${H_end_adj.x - 15} ${H_end_adj.y + 15} ${H_end_adj.x} ${H_end_adj.y}" stroke="#6495c8" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
        
        <!-- I and J are paired - both use audio2 - Light green color -->
        <path d="M ${leftJunction.x} 211 C ${leftJunction.x - 30} 230 ${leftJunction.x - eVisual * 0.7} 270 ${leftJunction.x - eVisual - 10} 300 C ${leftJunction.x - eVisual - 25} 315 ${I_end_adj.x + 15} ${I_end_adj.y - 15} ${I_end_adj.x} ${I_end_adj.y}" stroke="#4caf50" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
        <path d="M ${rightJunction.x} 211 C ${rightJunction.x + 30} 230 ${rightJunction.x + fVisual * 0.7} 270 ${rightJunction.x + fVisual + 10} 300 C ${rightJunction.x + fVisual + 25} 315 ${J_end_adj.x - 15} ${J_end_adj.y - 15} ${J_end_adj.x} ${J_end_adj.y}" stroke="#4caf50" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
        
        <!-- Connector boxes - G -->
        <g>
          ${connectors['G']?.image ? 
            `<image 
              href="${getConnectorImage(connectors['G'], connectors['G'].configuration || 'standard') || connectors['G'].image}"
              x="${G_end_adj.x - connW / 2}" 
              y="${G_end_adj.y - connH / 2}" 
              width="${connW}" 
              height="${connH}" 
              filter="url(#shadow)"
            />` :
            `<rect x="${G_end_adj.x - connW / 2}" y="${G_end_adj.y - connH / 2}" width="${connW}" height="${connH}" rx="6" fill="#6495c8" stroke="#fff" strokeWidth="3" filter="url(#shadow)" />`
          }
          <text x="${G_end_adj.x}" y="${G_end_adj.y - 30}" fontSize="14" fill="#000" fontWeight="bold" textAnchor="middle" dominantBaseline="middle">G</text>
        </g>
        
        <!-- Connector boxes - H -->
        <g>
          ${connectors['H']?.image ? 
            `<image 
              href="${getConnectorImage(connectors['H'], connectors['H'].configuration || 'standard') || connectors['H'].image}"
              x="${H_end_adj.x - connW / 2}" 
              y="${H_end_adj.y - connH / 2}" 
              width="${connW}" 
              height="${connH}" 
              filter="url(#shadow)"
            />` :
            `<rect x="${H_end_adj.x - connW / 2}" y="${H_end_adj.y - connH / 2}" width="${connW}" height="${connH}" rx="6" fill="#6495c8" stroke="#fff" strokeWidth="3" filter="url(#shadow)" />`
          }
          <text x="${H_end_adj.x}" y="${H_end_adj.y - 30}" fontSize="14" fill="#000" fontWeight="bold" textAnchor="middle" dominantBaseline="middle">H</text>
        </g>
        
        <!-- Connector boxes - I -->
        <g>
          ${connectors['I']?.image ? 
            `<image 
              href="${getConnectorImage(connectors['I'], connectors['I'].configuration || 'standard') || connectors['I'].image}"
              x="${I_end_adj.x - connW / 2}" 
              y="${I_end_adj.y - connH / 2}" 
              width="${connW}" 
              height="${connH}" 
              filter="url(#shadow)"
            />` :
            `<rect x="${I_end_adj.x - connW / 2}" y="${I_end_adj.y - connH / 2}" width="${connW}" height="${connH}" rx="6" fill="#4caf50" stroke="#fff" strokeWidth="3" filter="url(#shadow)" />`
          }
          <text x="${I_end_adj.x}" y="${I_end_adj.y + 40}" fontSize="14" fill="#000" fontWeight="bold" textAnchor="middle" dominantBaseline="middle">I</text>
        </g>
        
        <!-- Connector boxes - J -->
        <g>
          ${connectors['J']?.image ? 
            `<image 
              href="${getConnectorImage(connectors['J'], connectors['J'].configuration || 'standard') || connectors['J'].image}"
              x="${J_end_adj.x - connW / 2}" 
              y="${J_end_adj.y - connH / 2}" 
              width="${connW}" 
              height="${connH}" 
              filter="url(#shadow)"
            />` :
            `<rect x="${J_end_adj.x - connW / 2}" y="${J_end_adj.y - connH / 2}" width="${connW}" height="${connH}" rx="6" fill="#4caf50" stroke="#fff" strokeWidth="3" filter="url(#shadow)" />`
          }
          <text x="${J_end_adj.x}" y="${J_end_adj.y + 40}" fontSize="14" fill="#000" fontWeight="bold" textAnchor="middle" dominantBaseline="middle">J</text>
        </g>
        
        <!-- Overall dimension line - Professional style -->
        <line x1="${leftmostX}" y1="${overallLineY}" x2="${rightmostX}" y2="${overallLineY}" stroke="#bbb" strokeWidth="2" />
        <line x1="${leftmostX}" y1="${overallLineY-5}" x2="${leftmostX}" y2="${overallLineY+5}" stroke="#bbb" strokeWidth="2" />
        <line x1="${rightmostX}" y1="${overallLineY-5}" x2="${rightmostX}" y2="${overallLineY+5}" stroke="#bbb" strokeWidth="2" />
        <rect x="${(leftmostX + rightmostX) / 2 - 48}" y="${overallLabelY - 20}" width="96" height="24" rx="8" fill="white" stroke="#bbb" strokeWidth="1" />
        <text x="${(leftmostX + rightmostX) / 2}" y="${overallLabelY - 8}" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500" dominantBaseline="middle">
          ${unit === 'ft' ? `A = ${parseFloat(overallLen).toFixed(2).replace(/\.00$/, '')}'` : `A = ${overallLen}"`}
        </text>
        
        <!-- Length labels with oval outlined boxes - no dimension lines -->
        <!-- B label positioned on the middle wire - centered between junctions -->
        <rect x="${(leftJunction.x + rightJunction.x) / 2 - 48}" y="206 - 10" width="96" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
        <text x="${(leftJunction.x + rightJunction.x) / 2}" y="206 + 5" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
          ${unit === 'ft' ? `B = ${parseFloat(lengths['B']).toFixed(2).replace(/\.00$/, '')}'` : `B = ${parseFloat(lengths['B']) || 0}"`}
        </text>
        
        <rect x="${cMid.x-32}" y="${cMid.y+40}" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
        <text x="${cMid.x}" y="${cMid.y+55}" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
          ${lengths['C'] ? (unit === 'ft' ? `C = ${parseFloat(lengths['C']).toFixed(2).replace(/\.00$/, '')}'` : `C = ${lengths['C']}"`) : 'C'}
        </text>
        
        <rect x="${dMid.x-32}" y="${dMid.y+40}" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
        <text x="${dMid.x}" y="${dMid.y+55}" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
          ${lengths['D'] ? (unit === 'ft' ? `D = ${parseFloat(lengths['D']).toFixed(2).replace(/\.00$/, '')}'` : `D = ${lengths['D']}"`) : 'D'}
        </text>
        
        <rect x="${eMid.x-32}" y="${eMid.y+80}" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
        <text x="${eMid.x}" y="${eMid.y+95}" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
          ${lengths['E'] ? (unit === 'ft' ? `E = ${parseFloat(lengths['E']).toFixed(2).replace(/\.00$/, '')}'` : `E = ${lengths['E']}"`) : 'E'}
        </text>
        
        <rect x="${fMid.x-32}" y="${fMid.y+80}" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
        <text x="${fMid.x}" y="${fMid.y+95}" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
          ${lengths['F'] ? (unit === 'ft' ? `F = ${parseFloat(lengths['F']).toFixed(2).replace(/\.00$/, '')}'` : `F = ${lengths['F']}"`) : 'F'}
        </text>
      </g>
    </svg>
  `;
  
  return svgContent;
}

// Generate SVG for PSHM cables (4 connectors: G,H,I,J with RCA and Speakon)
export function generatePSHMSVG({
  connectors = {},
  lengths = {},
  unit = 'in',
  svgProps = {},
  asString = false
}) {
  
  // Calculate visual lengths with consistent scaling
  const cLen = PX_PER_INCH * (parseFloat(lengths['C']) || 0);
  const dLen = PX_PER_INCH * (parseFloat(lengths['D']) || 0);
  const eLen = PX_PER_INCH * (parseFloat(lengths['E']) || 0);
  const fLen = PX_PER_INCH * (parseFloat(lengths['F']) || 0);
  
  // Apply visual length caps for reasonable SVG display
  const cVisual = Math.min(cLen, MAX_VISUAL_LENGTH);
  const dVisual = Math.min(dLen, MAX_VISUAL_LENGTH);
  const eVisual = Math.min(eLen, MAX_VISUAL_LENGTH);
  const fVisual = Math.min(fLen, MAX_VISUAL_LENGTH);
  
  // Calculate endpoints with proper scaling for PSHM layout
  const G_end = { x: 200 - cVisual - 50, y: 120 };  // RCA Male (RA)
  const H_end = { x: 750 + dVisual + 50, y: 120 };  // RCA Female (STD)
  const I_end = { x: 200 - eVisual - 50, y: 380 };  // Speakon 2-Pole Male (P)
  const J_end = { x: 750 + fVisual + 50, y: 380 };  // Speakon 2-Pole Male (STD)
  
  // Auto-scaling logic
  const pad = 30;
  const minX = Math.min(G_end.x, I_end.x);
  const maxX = Math.max(H_end.x, J_end.x);
  const contentWidth = maxX - minX;
  
  const minY = Math.min(G_end.y, H_end.y, 40) - 40 - 15;
  const maxY = Math.max(I_end.y, J_end.y) + 40 + 15;
  const contentHeight = maxY - minY;
  
  const viewBoxWidth = contentWidth + 2 * pad;
  const viewBoxHeight = contentHeight + 2 * pad;
  const viewBoxX = minX - pad;
  const viewBoxY = minY - pad;
  
  // Dynamic label positions
  const cLabel = { x: 200 - (cVisual * 0.6), y: 200 - (cVisual * 0.3) };
  const dLabel = { x: 750 + (dVisual * 0.6), y: 200 - (dVisual * 0.3) };
  const eLabel = { x: 200 - (eVisual * 0.6), y: 300 + (eVisual * 0.3) };
  const fLabel = { x: 750 + (fVisual * 0.6), y: 300 + (eVisual * 0.3) };
  
  const overallLen = parseFloat(lengths['A']) || 0;
  
  // Precompute all math for SVG attributes
  const rectY = 251 - 10;
  const textY = 251 + 5;
  const rectX = (200 + 750) / 2 - 48;
  const textX = (200 + 750) / 2;
  
  // Debug connector images
  ['G', 'H', 'I', 'J'].forEach(pointId => {
    const connector = connectors[pointId];
    if (connector?.image) {
      const imagePath = getConnectorImage(connector, connector?.configuration || 'standard');
    }
  });
  
  // Generate the SVG content
  const svgContent = `
    <svg width="1000" height="500" viewBox="${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}" xmlns="http://www.w3.org/2000/svg" style="font-family: Arial, sans-serif;">
      <defs>
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="2" dy="3" stdDeviation="3" floodColor="#000" floodOpacity="0.3" />
        </filter>
      </defs>
      
      <g>
        <!-- Central wire (B) - Represents the main cable length -->
        <path d="M 200 251 L 750 251" stroke="#6495c8" strokeWidth="8" fill="none" filter="url(#shadow)" />
        
        <!-- Cable sheath outline for better visibility -->
        <rect x="200" y="245" width="550" height="12" rx="6" fill="#6495c8" stroke="#b32b2b" strokeWidth=".5" opacity="0.7" />
        
        <!-- RCA branches (G, H) - Blue, thin with prettier curves -->
        <path d="M 200 251 C ${200 - 30} 230 ${200 - cVisual * 0.7} 190 ${200 - cVisual - 10} 160 C ${200 - cVisual - 25} 145 ${G_end.x + 15} ${G_end.y + 15} ${G_end.x} ${G_end.y}" stroke="#6495c8" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
        <path d="M 750 251 C ${750 + 30} 230 ${750 + dVisual * 0.7} 190 ${750 + dVisual + 10} 160 C ${750 + dVisual + 25} 145 ${H_end.x - 15} ${H_end.y + 15} ${H_end.x} ${H_end.y}" stroke="#6495c8" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
        
        <!-- Speakon branches (I, J) - Green, thin with prettier curves -->
        <path d="M 200 251 C ${200 - 30} 270 ${200 - eVisual * 0.7} 310 ${200 - eVisual - 10} 340 C ${200 - eVisual - 25} 355 ${I_end.x + 15} ${I_end.y - 15} ${I_end.x} ${I_end.y}" stroke="#4caf50" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
        <path d="M 750 251 C ${750 + 30} 270 ${750 + fVisual * 0.7} 310 ${750 + fVisual + 10} 340 C ${750 + fVisual + 25} 355 ${J_end.x - 15} ${J_end.y - 15} ${J_end.x} ${J_end.y}" stroke="#4caf50" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
        
        <!-- Connector points -->
        ${['G', 'H', 'I', 'J'].map(pointId => {
          const endPos = pointId === 'G' ? G_end :
                        pointId === 'H' ? H_end :
                        pointId === 'I' ? I_end :
                        J_end;
          const color = pointId === 'G' || pointId === 'H' ? '#6495c8' : '#4caf50';
          const connector = connectors[pointId];
          
          // Get connector label for display
          const getConnectorLabel = (conn) => {
            if (!conn) return pointId;
            
            // Try to get a meaningful label from the connector
            if (conn.label) return conn.label;
            if (conn.family && conn.gender) {
              return `${conn.family} ${conn.gender}`;
            }
            if (conn.id) return conn.id;
            
            return pointId;
          };
          
          return `
            <g>
              <rect x="${endPos.x - connW / 2}" y="${endPos.y - connH / 2}" width="${connW}" height="${connH}" rx="6" fill="${color}" stroke="#fff" strokeWidth="3" filter="url(#shadow)" />
              <text x="${endPos.x}" y="${endPos.y}" textAnchor="middle" fontSize="12" fill="#fff" fontWeight="bold" dominantBaseline="middle">
                ${getConnectorLabel(connector)}
              </text>
              <text x="${endPos.x}" y="${endPos.y + connH / 2 + 20}" textAnchor="middle" fontSize="14" fill="#000" fontWeight="bold">${pointId}</text>
            </g>
          `;
        }).join('')}
        
        <!-- Overall length line and label -->
        <line x1="${minX}" y1="90" x2="${maxX}" y2="90" stroke="#bbb" strokeWidth="2" />
        <line x1="${minX}" y1="85" x2="${minX}" y2="95" stroke="#bbb" strokeWidth="2" />
        <line x1="${maxX}" y1="85" x2="${maxX}" y2="95" stroke="#bbb" strokeWidth="2" />
        <rect x="${rectX}" y="${rectY}" width="96" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
        <text x="${textX}" y="${textY}" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500" dominantBaseline="middle">
          ${unit === 'ft' ? `A = ${parseFloat(overallLen).toFixed(2).replace(/\.00$/, '')}'` : `A = ${overallLen}"`}
        </text>
        
        <!-- B length trunk line - positioned on the middle wire -->
        <rect x="${rectX}" y="${rectY}" width="96" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
        <text x="${textX}" y="${textY}" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
          ${unit === 'ft' ? `B = ${parseFloat(lengths['B']).toFixed(2).replace(/\.00$/, '')}'` : `B = ${parseFloat(lengths['B']) || 0}"`}
        </text>
        
        <!-- Length labels -->
        <rect x="${cLabel.x-32}" y="${cLabel.y-10}" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
        <text x="${cLabel.x}" y="${cLabel.y+5}" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
          ${lengths['C'] ? (unit === 'ft' ? `C = ${parseFloat(lengths['C']).toFixed(2).replace(/\.00$/, '')}'` : `C = ${lengths['C']}"`) : 'C'}
        </text>
        <rect x="${dLabel.x-32}" y="${dLabel.y-10}" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
        <text x="${dLabel.x}" y="${dLabel.y+5}" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
          ${lengths['D'] ? (unit === 'ft' ? `D = ${parseFloat(lengths['D']).toFixed(2).replace(/\.00$/, '')}'` : `D = ${lengths['D']}"`) : 'D'}
        </text>
        <rect x="${eLabel.x-32}" y="${eLabel.y-10}" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
        <text x="${eLabel.x}" y="${eLabel.y+5}" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
          ${lengths['E'] ? (unit === 'ft' ? `E = ${parseFloat(lengths['E']).toFixed(2).replace(/\.00$/, '')}'` : `E = ${lengths['E']}"`) : 'E'}
        </text>
        <rect x="${fLabel.x-32}" y="${fLabel.y-10}" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
        <text x="${fLabel.x}" y="${fLabel.y+5}" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
          ${lengths['F'] ? (unit === 'ft' ? `F = ${parseFloat(lengths['F']).toFixed(2).replace(/\.00$/, '')}'` : `F = ${lengths['F']}"`) : 'F'}
        </text>
      </g>
    </svg>
  `;
  
  return svgContent;
}

// Note: Cable type parsing functions removed as they were unused
// const parseCableTypeFromSku = (sku) => { ... };
// const parseCableTypeFromConnectors = (connectors) => { ... };

// Generate SVG for simple end-to-end cables
export function generateSimpleCableSVG({
  connectors = {},
  length = 0,
  unit = 'in',
  cableType = 'audio',
  sku = null,
  svgProps = {},
  asString = false
}) {
  // Calculate visual length with consistent scaling
  const PX_PER_INCH = 6;
  const MAX_VISUAL_LENGTH = 400;
  const lengthPx = PX_PER_INCH * (parseFloat(length) || 0);
  const visualLength = Math.min(lengthPx, MAX_VISUAL_LENGTH);
  // Ensure minimum visual length for very short cables
  const minVisualLength = 150;
  const finalVisualLength = Math.max(visualLength, minVisualLength);
  
  // Calculate positions to match BaseConnectorSelector layout
  const padding = 80;
  const connectorSize = 70; // Match the 70x70 boxes from BaseConnectorSelector
  const cableY = 250;
  const startX = padding + connectorSize / 2;
  const endX = startX + finalVisualLength;
  const totalWidth = endX + padding + connectorSize / 2;
  const totalHeight = 400;
  
  // Auto-scaling viewBox
  const viewBoxX = 0;
  const viewBoxY = 0;
  const viewBoxWidth = totalWidth;
  const viewBoxHeight = totalHeight;
  
  // Note: Cable type determination removed as it was unused
  // let finalCableType = cableType || 'audio';
  // Note: Cable type determination logic removed as it was unused
  // if (sku) {
  //   finalCableType = parseCableTypeFromSku(sku);
  // } else if (connectors) {
  //   // IEC, NEMA, PowerCon, True1, etc. should be 'power'
  //   const a = connectors.endA;
  //   const b = connectors.endB;
  //   const isPower = (conn) => {
  //     if (!conn) return false;
  //     const fam = (conn.family || '').toLowerCase();
  //     const id = (conn.id || '').toLowerCase();
  //     return fam.includes('iec') || fam.includes('power') || fam.includes('nema') || fam.includes('true1') || id.includes('iec') || id.includes('power') || id.includes('nema') || id.includes('true1');
  //   };
  //   if (isPower(a) || isPower(b)) finalCableType = 'power';
  //   else finalCableType = parseCableTypeFromConnectors(connectors);
  // }
  
  // Helper function to get connector image based on configuration (copied from BaseConnectorSelector)
  const getConnectorImage = (connector, configuration) => {
    if (!connector?.image) return connector?.image;
    
    // Handle XLR connectors (3-pin and 5-pin)
    if (connector.family === 'XLR' || connector.id?.startsWith('X') || connector.id?.startsWith('NC3') || connector.id?.startsWith('NC5')) {
      if (configuration === 'right-angle') {
        if (connector.gender?.toLowerCase() === 'male') {
          return '/images/nc3mrx.jpg';
        }
        if (connector.gender?.toLowerCase() === 'female') {
          return '/images/nc3frx.jpg';
        }
      } else if (configuration === 'panel-mount') {
        if (connector.gender?.toLowerCase() === 'male') {
          return '/images/nc3md-l-1.jpg';
        }
        if (connector.gender?.toLowerCase() === 'female') {
          return '/images/nc3fd-L-1.jpg';
        }
      }
      // Standard XLR - return the original image
      return connector.image;
    }
    
    // Handle 1/4" TS connectors
    if (connector.family === 'TS' || connector.id === '2T_M' || connector.id === '2T_F') {
      if (connector.gender?.toLowerCase() === 'male') {
        if (configuration === 'right-angle') {
          return '/images/1_4_ TS-RA (ACPM-TN).png';
        } else if (configuration === 'panel-mount') {
          return '/images/coming_soon.png'; // Placeholder for panel mount
        } else {
          return '/images/1_4_ TS (ACPM-GN).png'; // Standard
        }
      } else {
        // All female variants use placeholder
        return '/images/coming_soon.png';
      }
    }
    
    // Handle 1/4" TRS connectors
    if (connector.family === 'TRS' || connector.id === '2TR_M' || connector.id === '2TR_F') {
      if (connector.gender?.toLowerCase() === 'male') {
        if (configuration === 'right-angle') {
          return '/images/1_4_ TRS-RA (ACPS-TN).png';
        } else if (configuration === 'panel-mount') {
          return '/images/coming_soon.png'; // Placeholder for panel mount
        } else {
          return '/images/1_4_ TRS (ACPS-GN).png'; // Standard
        }
      } else {
        // All female variants use placeholder
        return '/images/coming_soon.png';
      }
    }
    
    // Handle 3.5mm TS connectors
    if (connector.id === '3T_M' || connector.id === '3T_F') {
      if (connector.gender?.toLowerCase() === 'male') {
        if (configuration === 'right-angle') {
          return '/images/coming_soon.png'; // Placeholder for right angle
        } else if (configuration === 'panel-mount') {
          return '/images/coming_soon.png'; // Placeholder for panel mount
        } else {
          return '/images/TS_3_5mm_male.png'; // Standard
        }
      } else {
        // All female variants use placeholder
        return '/images/coming_soon.png';
      }
    }
    
    // Handle 3.5mm TRS connectors
    if (connector.id === '3TR_M' || connector.id === '3TR_F') {
      if (connector.gender?.toLowerCase() === 'male') {
        if (configuration === 'right-angle') {
          return '/images/coming_soon.png'; // Placeholder for right angle
        } else if (configuration === 'panel-mount') {
          return '/images/coming_soon.png'; // Placeholder for panel mount
        } else {
          return '/images/TRS_3_5mm_male.png'; // Standard
        }
      } else {
        if (configuration === 'right-angle') {
          return '/images/coming_soon.png'; // Placeholder for right angle
        } else if (configuration === 'panel-mount') {
          return '/images/coming_soon.png'; // Placeholder for panel mount
        } else {
          return '/images/TRS_3_5mm_female.png'; // Standard
        }
      }
    }
    
    // Handle RCA connectors
    if (connector.family === 'RCA' || connector.id === 'RCA_M' || connector.id === 'RCA_F') {
      if (configuration === 'right-angle') {
        return '/images/coming_soon.png'; // Placeholder for right angle
      } else if (configuration === 'panel-mount') {
        return '/images/coming_soon.png'; // Placeholder for panel mount
      } else {
        // Standard RCA - return the original image
        return connector.image;
      }
    }
    
    // Handle Speakon connectors (no right-angle variants available)
    if (connector.family === 'Speakon' || connector.id?.startsWith('SPK')) {
      if (configuration === 'right-angle') {
        return '/images/coming_soon.png'; // Placeholder for right angle (not available)
      } else if (configuration === 'panel-mount') {
        return '/images/coming_soon.png'; // Placeholder for panel mount
      } else {
        // Standard Speakon - return the original image
        return connector.image;
      }
    }
    
    // Handle PowerCON connectors
    if (connector.family?.includes('PowerCon') || connector.id?.includes('PowerCon')) {
      if (configuration === 'right-angle') {
        return '/images/coming_soon.png'; // Placeholder for right angle
      } else if (configuration === 'panel-mount') {
        if (connector.family === 'PowerCon_Blue' || connector.id?.includes('Blue')) {
          return '/images/NAC3MPXXA.jpg'; // Blue panel mount male
        } else {
          return '/images/NAC3MPXXB.jpg'; // Grey panel mount female
        }
      } else {
        // Standard configuration
        if (connector.family === 'PowerCon_Blue' || connector.id?.includes('Blue')) {
          return '/images/NAC3FXXA-W-S.png'; // Blue standard female
        } else {
          return '/images/NAC3FXXB-W-S.png'; // Grey standard male
        }
      }
    }
    
    // Handle IEC connectors
    if (connector.family?.includes('IEC') || connector.id?.includes('IEC')) {
      if (configuration === 'right-angle') {
        return '/images/coming_soon.png'; // Placeholder for right angle
      } else if (configuration === 'panel-mount') {
        if (connector.gender?.toLowerCase() === 'male') {
          return '/images/IEC_C14_panel_mount_male.png';
        } else if (connector.gender?.toLowerCase() === 'female') {
          return '/images/IEC_C13_panel_mount_female.png';
        }
      } else {
        // Standard IEC - return the original image
        return connector.image;
      }
    }
    
    // Handle NEMA 5-15 connectors
    if (connector.family?.includes('NEMA') || connector.id?.includes('5-15')) {
      if (configuration === 'right-angle') {
        return '/images/coming_soon.png'; // Placeholder for right angle
      } else if (configuration === 'panel-mount') {
        if (connector.gender?.toLowerCase() === 'male') {
          return '/images/515P_M.jpg';
        } else if (connector.gender?.toLowerCase() === 'female') {
          return '/images/515R_F.jpg';
        }
      } else {
        // Standard NEMA - return the original image
        return connector.image;
      }
    }
    
    // Handle True1 connectors
    if (connector.family?.includes('True1') || connector.id?.includes('True1')) {
      if (configuration === 'right-angle') {
        return '/images/coming_soon.png'; // Placeholder for right angle
      } else if (configuration === 'panel-mount') {
        return '/images/coming_soon.png'; // Placeholder for panel mount
      } else {
        // Standard True1 - return the original image
        return connector.image;
      }
    }
    
    // Default fallback - return the original image
    return connector.image;
  };
  
  // Get connector images and labels
  const getConnectorLabel = (conn) => {
    if (!conn) return '';
    if (conn.label) return conn.label;
    if (conn.family && conn.gender) {
      return `${conn.family} ${conn.gender}`;
    }
    if (conn.id) return conn.id;
    return '';
  };
  
  const endAImg = getConnectorImage(connectors.endA, connectors.endA?.configuration || 'standard');
  const endBImg = getConnectorImage(connectors.endB, connectors.endB?.configuration || 'standard');
  const endALabel = getConnectorLabel(connectors.endA);
  const endBLabel = getConnectorLabel(connectors.endB);
  
  // SVG content matching BaseConnectorSelector design
  const svgContent = `
    <svg width="${totalWidth}" height="${totalHeight}" viewBox="${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}" xmlns="http://www.w3.org/2000/svg" style="font-family: Arial, sans-serif;">
      <defs>
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="2" dy="3" stdDeviation="3" floodColor="#000" floodOpacity="0.3" />
        </filter>
      </defs>
      
      <g>
        <!-- Background paper matching BaseConnectorSelector -->
        <rect x="0" y="0" width="${totalWidth}" height="${totalHeight}" fill="#f8f9fa" rx="8" />
        
        <!-- End A Connector Box - matching BaseConnectorSelector styling -->
        <g>
          <rect x="${startX-connectorSize/2}" y="${cableY-connectorSize/2}" width="${connectorSize}" height="${connectorSize}" rx="8" fill="${connectors.endA?.connector ? '#e8f5e8' : '#f5f5f5'}" stroke="#4caf50" stroke-width="2" filter="url(#shadow)" />
          ${endAImg ? 
            `<image href="${endAImg}" x="${startX-connectorSize/2+5}" y="${cableY-connectorSize/2+5}" width="${connectorSize-10}" height="${connectorSize-10}" style="border-radius: 6px;" />` :
            `<text x="${startX}" y="${cableY}" text-anchor="middle" font-size="24" fill="#666" font-weight="bold" dominant-baseline="middle">A</text>`
          }
        </g>
        
        <!-- Wire visualization - matching BaseConnectorSelector -->
        <g>
          <!-- Left wire segment -->
          <rect x="${startX + connectorSize/2}" y="${cableY-4}" width="60" height="8" rx="4" fill="linear-gradient(90deg, #4a4a4a 0%, #666 50%, #4a4a4a 100%)" filter="url(#shadow)" />
          
          <!-- Directional arrow -->
          <polygon points="${startX + connectorSize/2 + 60},${cableY-4} ${startX + connectorSize/2 + 66},${cableY} ${startX + connectorSize/2 + 60},${cableY+4}" fill="#b32b2b" filter="url(#shadow)" />
          
          <!-- Right wire segment -->
          <rect x="${startX + connectorSize/2 + 66}" y="${cableY-4}" width="60" height="8" rx="4" fill="linear-gradient(90deg, #4a4a4a 0%, #666 50%, #4a4a4a 100%)" filter="url(#shadow)" />
        </g>
        
        <!-- End B Connector Box - matching BaseConnectorSelector styling -->
        <g>
          <rect x="${endX-connectorSize/2}" y="${cableY-connectorSize/2}" width="${connectorSize}" height="${connectorSize}" rx="8" fill="${connectors.endB?.connector ? '#e8f5e8' : '#f5f5f5'}" stroke="#4caf50" stroke-width="2" filter="url(#shadow)" />
          ${endBImg ? 
            `<image href="${endBImg}" x="${endX-connectorSize/2+5}" y="${cableY-connectorSize/2+5}" width="${connectorSize-10}" height="${connectorSize-10}" style="border-radius: 6px;" />` :
            `<text x="${endX}" y="${cableY}" text-anchor="middle" font-size="24" fill="#666" font-weight="bold" dominant-baseline="middle">B</text>`
          }
        </g>
        
        <!-- Descriptions below connectors - matching BaseConnectorSelector -->
        <g>
          <text x="${startX}" y="${cableY+connectorSize/2+20}" text-anchor="middle" font-size="12" fill="#666" font-weight="500">
            ${endALabel || 'Select End A...'}
          </text>
          <text x="${endX}" y="${cableY+connectorSize/2+20}" text-anchor="middle" font-size="12" fill="#666" font-weight="500">
            ${endBLabel || 'Select End B...'}
          </text>
        </g>
        
        <!-- Length measurement above cable -->
        <g>
          <line x1="${startX + connectorSize/2}" y1="${cableY-60}" x2="${endX - connectorSize/2}" y2="${cableY-60}" stroke="#bbb" stroke-width="2" />
          <line x1="${startX + connectorSize/2}" y1="${cableY-65}" x2="${startX + connectorSize/2}" y2="${cableY-55}" stroke="#bbb" stroke-width="2" />
          <line x1="${endX - connectorSize/2}" y1="${cableY-65}" x2="${endX - connectorSize/2}" y2="${cableY-55}" stroke="#bbb" stroke-width="2" />
          <rect x="${(startX+endX)/2-40}" y="${cableY-75}" width="80" height="20" rx="6" fill="white" stroke="#bbb" stroke-width="1" />
          <text x="${(startX+endX)/2}" y="${cableY-65}" text-anchor="middle" font-size="14" fill="#666" font-weight="500" dominant-baseline="middle">
            ${unit === 'ft' ? `${parseFloat(length).toFixed(2).replace(/\.00$/, '')}'` : `${length}"`}
          </text>
        </g>
              </g>
      </svg>
    `;
    
    return svgContent;
  }

// Helper to base64-embed all connector images in a connectors object
async function base64EmbedConnectors(connectors) {
  if (!connectors) return connectors;
  const out = { ...connectors };
  for (const key of Object.keys(connectors)) {
    const conn = connectors[key];
    if (conn && conn.image) {
      try {
        out[key] = { ...conn, image: await imageUrlToBase64DataUri(conn.image) };
      } catch (e) {
        out[key] = { ...conn };
      }
    }
  }
  return out;
}

// Async version for hybrid cables
export async function generateHybridSVGAsync({ connectors = {}, lengths = {}, unit = 'in', svgProps = {}, asString = false }) {
  const base64Connectors = await base64EmbedConnectors(connectors);
  return generateHybridSVG({ connectors: base64Connectors, lengths, unit, svgProps, asString });
}

export async function generateHybridPowerAudioAudioSVGAsync({ connectors = {}, lengths = {}, unit = 'in', svgProps = {}, asString = false }) {
  const base64Connectors = await base64EmbedConnectors(connectors);
  return generateHybridPowerAudioAudioSVG({ connectors: base64Connectors, lengths, unit, svgProps, asString });
}

export async function generateWyeSVGAsync({ connectors = {}, lengths = {}, unit = 'in', connectorType = 'audio', svgProps = {}, asString = false }) {
  const base64Connectors = await base64EmbedConnectors(connectors);
  return generateWyeSVG({ connectors: base64Connectors, lengths, unit, connectorType, svgProps, asString });
}

export async function generateZipAudioAudioSVGAsync({ connectors = {}, lengths = {}, unit = 'in', svgProps = {}, asString = false }) {
  const base64Connectors = await base64EmbedConnectors(connectors);
  return generateZipAudioAudioSVG({ connectors: base64Connectors, lengths, unit, svgProps, asString });
}

export async function generatePSHMSVGAsync({ connectors = {}, lengths = {}, unit = 'in', svgProps = {}, asString = false }) {
  const base64Connectors = await base64EmbedConnectors(connectors);
  return generatePSHMSVG({ connectors: base64Connectors, lengths, unit, svgProps, asString });
}

// Main function to generate SVG based on cable type (now async, all types)
export async function generateCableSVG(config) {
  const { style, hybridConnectors, connectors, length, cableType, unit = 'in', sku } = config;
  const isPSHMCable = () => {
    const connectors = hybridConnectors?.connectors || {};
    const hasPSHMConnectors = connectors.G && connectors.H && connectors.I && connectors.J;
    return hasPSHMConnectors;
  };
  const isSimpleCable = () => {
    return style === 'end-to-end' && connectors && connectors.endA && connectors.endB;
  };
  if (isSimpleCable()) {
    return await generateSimpleCableSVGAsync({
      connectors: connectors,
      length: length,
      unit: unit,
      cableType: cableType || 'audio',
      sku: sku
    });
  }
  if (isPSHMCable()) {
    return await generatePSHMSVGAsync({
      connectors: hybridConnectors?.connectors || {},
      lengths: hybridConnectors?.lengths || {},
      unit
    });
  }
  switch (style) {
    case 'hybrid':
      return await generateHybridSVGAsync({
        connectors: hybridConnectors?.connectors || {},
        lengths: hybridConnectors?.lengths || {},
        unit
      });
    case 'hybrid-power-audio-audio':
      return await generateHybridPowerAudioAudioSVGAsync({
        connectors: hybridConnectors?.connectors || {},
        lengths: hybridConnectors?.lengths || {},
        unit
      });
    case 'wye':
      return await generateWyeSVGAsync({
        connectors: hybridConnectors?.connectors || {},
        lengths: hybridConnectors?.lengths || {},
        unit,
        connectorType: hybridConnectors?.connectorType || 'audio'
      });
    case 'zip-audio-audio':
      return await generateZipAudioAudioSVGAsync({
        connectors: hybridConnectors?.connectors || {},
        lengths: hybridConnectors?.lengths || {},
        unit
      });
    default:
      return await generateHybridPowerAudioAudioSVGAsync({
        connectors: hybridConnectors?.connectors || {},
        lengths: hybridConnectors?.lengths || {},
        unit
      });
  }
} 

// Add this async wrapper near the other async SVG functions:
export async function generateSimpleCableSVGAsync(args) {
  // No async work needed, just call the sync function
  return generateSimpleCableSVG(args);
}